// The words of the build line, the build bar and the done moment.
//
// Namespace `build-experience`; owner: DES2 (components in ui/workspace/build/). The phase
// words describe what is happening in plain language: never a sandbox, VM or engine name, and
// never invented narration for an outside agent. Placeholders: {step}, {seconds}, {minutes},
// {n}, {total}, {model}, {agent}, {kind}, {min}, {s}. See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  phaseQueued: 'Waiting to start',
  phaseAcquiring: 'Getting a workspace ready',
  phaseAuthoring: 'Writing',
  phaseCompiling: 'Putting it together',
  phaseRendering: 'Drawing previews',
  phaseChecking: 'Checking that it works',
  phaseSaving: 'Saving',
  phaseSettled: 'Done',
  stepOf: '{n} of {total}',
  stillWorking: 'Still working on {step}',
  slowReason: '{step} is taking longer than usual.',
  keepWaiting: 'Keep waiting',
  stop: 'Stop',
  stopping: 'Stopping…',
  continue: 'Continue',
  fix: 'Fix it',
  fixInChat: 'Fix in chat',
  remoteWorking: '{agent} is working',
  remoteWorkingUnnamed: 'The connected agent is working',
  remoteLastSecond: 'Last step {seconds} s ago',
  remoteLastMinute: 'Last step {minutes} min ago',
  remoteNoStep: 'No step reported yet',
  builtWith: 'Built with {model}',
  buildStartedChip: 'Build started',
  approvedBuilding: 'Approved · Building…',
  built: 'Built',
  byAgent: 'via {agent}',
  agentNameReported: 'Name reported by the agent',
  publishedBy: 'Published by {agent}',
  elapsed: 'Time so far',
  ready: '{kind} is ready',
  readyGeneric: 'Ready',
  publishing: 'Publishing…',
  fieldsCount: '{n} fields',
  confirming: 'Confirming it was saved',
  steps: 'Steps',
  outcomeUnknown: 'It may have finished, but that couldn’t be confirmed.',
  checkAgain: 'Check again',
  stopHere: 'Stop here',
  budgetPaused:
    'Paused at the budget: {spent} of {limit}. The last working version stays as a draft.',
  continueWith: 'Continue with {amount} more',
  failed: 'It stopped before it was ready.',
  stopped: 'Stopped. The last saved version is unchanged.',
  stoppedNeutral: 'Stopped.',
  blockedApproval: 'Waiting for your OK',
  blockedBudget: 'Paused at this build’s budget',
  blockedProvider: 'The model service isn’t answering right now',
  blockedViewer: 'Someone needs to be set up to run this for viewers',
  blockedOther: 'Paused',
  denied: 'You don’t have access to this build.',
  loading: 'Starting…',
  shaping: 'Shaping it from your request…',
  pendingValue: 'Not filled in yet',
  unavailableValue: 'Not available',
  busyNow: '{model} is busy right now.',
  busyHeld: '{model} is busy.',
  busyStill: '{model} is still busy.',
  draftSaved: 'Your draft is saved.',
  retryIn: 'Trying again in {n} s.',
  retryingNow: 'Trying again now.',
  checkingLastStep: 'Checking whether the last step finished…',
  continueFrom: 'It will continue from “{step}” when {model} is free.',
  tryLater: 'Try again in a few minutes.',
  stoppedAt: 'Stopped. Your draft is saved at “{step}”.',
  tryNow: 'Try now',
  tryAgain: 'Try again',
  tryWith: 'Try with {model}',
  altPolicy: 'Another model isn’t allowed by your company’s settings.',
  altRegion: 'Another model isn’t available in your company’s processing region.',
  altBudget: 'Another model would go over your company’s budget.',
  altNotReady: 'Another model isn’t ready to use yet.',
  buildingWith: 'Building with {model}',
  buildingVersion: 'Building v{n} with {model}',
  versionKept: 'v{kept} stays as it is.',
  compareWith: 'Compare with v{n}',
  // DES1 / ARCH C20: builder not ready for this kind of Artifact (the kind-bound card names
  // the kind; the reasons map one to one to the picker's real blockedReason, anything
  // unmapped is builderNotReady) and progress
  builderNotReady: 'This kind of Artifact can’t be built in this workspace yet.',
  builderNotReadyState: 'Not ready yet',
  builderNothingStarted: 'No build was started or charged.',
  builderNotAllowed: 'Your company’s settings don’t allow building this kind of Artifact.',
  builderNotInRegion: 'This kind of Artifact can’t be built in your company’s processing region.',
  builderOverBudget: 'Building this kind of Artifact would go over your company’s budget.',
  buildLargerTakesMinutes: 'Larger builds take a few minutes.',
  startBlankChat: 'Start from a blank chat',
  elapsedMinSec: '{min} min {s} s',
};

export type BuildExperienceCopyKey = keyof typeof en;

const sv: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'Väntar på att starta',
  phaseAcquiring: 'Gör i ordning en arbetsyta',
  phaseAuthoring: 'Skriver',
  phaseCompiling: 'Sätter ihop',
  phaseRendering: 'Ritar förhandsvisningar',
  phaseChecking: 'Kontrollerar att det fungerar',
  phaseSaving: 'Sparar',
  phaseSettled: 'Klart',
  stepOf: '{n} av {total}',
  stillWorking: 'Jobbar fortfarande med {step}',
  slowReason: '{step} tar längre tid än vanligt.',
  keepWaiting: 'Fortsätt vänta',
  stop: 'Stoppa',
  stopping: 'Stoppar…',
  continue: 'Fortsätt',
  fix: 'Rätta det',
  fixInChat: 'Rätta i chatten',
  remoteWorking: '{agent} arbetar',
  remoteWorkingUnnamed: 'Den anslutna agenten arbetar',
  remoteLastSecond: 'Senaste steget för {seconds} s sedan',
  remoteLastMinute: 'Senaste steget för {minutes} min sedan',
  remoteNoStep: 'Inget steg rapporterat än',
  builtWith: 'Byggd med {model}',
  buildStartedChip: 'Bygget har startat',
  approvedBuilding: 'Godkänd · Bygger…',
  built: 'Byggd',
  byAgent: 'via {agent}',
  agentNameReported: 'Namnet som agenten uppger',
  publishedBy: 'Publicerad av {agent}',
  elapsed: 'Tid hittills',
  ready: '{kind} är klar',
  readyGeneric: 'Klart',
  publishing: 'Publicerar…',
  fieldsCount: '{n} fält',
  confirming: 'Bekräftar att det sparades',
  steps: 'Steg',
  outcomeUnknown: 'Det kan ha blivit klart, men det gick inte att bekräfta.',
  checkAgain: 'Kontrollera igen',
  stopHere: 'Stoppa här',
  budgetPaused:
    'Pausat vid budgeten: {spent} av {limit}. Den senast fungerande versionen sparas som utkast.',
  continueWith: 'Fortsätt med {amount} till',
  failed: 'Det stannade innan det var klart.',
  stopped: 'Stoppat. Den senast sparade versionen är oförändrad.',
  stoppedNeutral: 'Stoppat.',
  blockedApproval: 'Väntar på ditt OK',
  blockedBudget: 'Pausat vid byggets budget',
  blockedProvider: 'Modelltjänsten svarar inte just nu',
  blockedViewer: 'Någon behöver ställas in för att köra det här åt tittare',
  blockedOther: 'Pausat',
  denied: 'Du har inte åtkomst till det här bygget.',
  loading: 'Startar…',
  shaping: 'Formar det utifrån din förfrågan…',
  pendingValue: 'Inte ifyllt än',
  unavailableValue: 'Inte tillgängligt',
  busyNow: '{model} är upptagen just nu.',
  busyHeld: '{model} är upptagen.',
  busyStill: '{model} är fortfarande upptagen.',
  draftSaved: 'Ditt utkast är sparat.',
  retryIn: 'Försöker igen om {n} s.',
  retryingNow: 'Försöker igen nu.',
  checkingLastStep: 'Kontrollerar om det senaste steget blev klart…',
  continueFrom: 'Det fortsätter från steget ”{step}” när {model} är ledig.',
  tryLater: 'Försök igen om några minuter.',
  stoppedAt: 'Stoppat. Ditt utkast är sparat vid steget ”{step}”.',
  tryNow: 'Försök nu',
  tryAgain: 'Försök igen',
  tryWith: 'Försök med {model}',
  altPolicy: 'Din organisations inställningar tillåter inte en annan modell.',
  altRegion: 'En annan modell finns inte i din organisations behandlingsregion.',
  altBudget: 'En annan modell skulle gå över din organisations budget.',
  altNotReady: 'En annan modell är inte redo att användas än.',
  buildingWith: 'Bygger med {model}',
  buildingVersion: 'Bygger v{n} med {model}',
  versionKept: 'v{kept} förblir som den är.',
  compareWith: 'Jämför med v{n}',
  builderNotReady: 'Den här typen av artefakt kan inte byggas i den här arbetsytan än.',
  builderNotReadyState: 'Inte redo än',
  builderNothingStarted: 'Inget bygge startades eller debiterades.',
  builderNotAllowed:
    'Din organisations inställningar tillåter inte att den här typen av artefakt byggs.',
  builderNotInRegion:
    'Den här typen av artefakt kan inte byggas i din organisations behandlingsregion.',
  builderOverBudget: 'Att bygga den här typen av artefakt skulle gå över din organisations budget.',
  buildLargerTakesMinutes: 'Större byggen tar några minuter.',
  startBlankChat: 'Börja från en tom chatt',
  elapsedMinSec: '{min} min {s} s',
};

const bg: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'Изчакване на старта',
  phaseAcquiring: 'Подготовка на работно пространство',
  phaseAuthoring: 'Писане',
  phaseCompiling: 'Сглобяване',
  phaseRendering: 'Създаване на визуализации',
  phaseChecking: 'Проверка дали работи',
  phaseSaving: 'Запазване',
  phaseSettled: 'Готово',
  stepOf: '{n} от {total}',
  stillWorking: 'Още се работи по {step}',
  slowReason: '{step} отнема повече време от обикновено.',
  keepWaiting: 'Продължи да чакаш',
  stop: 'Спри',
  stopping: 'Спиране…',
  continue: 'Продължи',
  fix: 'Поправи',
  fixInChat: 'Поправи в чата',
  remoteWorking: '{agent} работи',
  remoteWorkingUnnamed: 'Свързаният агент работи',
  remoteLastSecond: 'Последна стъпка преди {seconds} сек',
  remoteLastMinute: 'Последна стъпка преди {minutes} мин',
  remoteNoStep: 'Все още няма отчетена стъпка',
  builtWith: 'Създадено с {model}',
  buildStartedChip: 'Изграждането започна',
  approvedBuilding: 'Одобрено · Създава се…',
  built: 'Създадено',
  byAgent: 'чрез {agent}',
  agentNameReported: 'Име, посочено от агента',
  publishedBy: 'Публикувано от {agent}',
  elapsed: 'Изминало време',
  ready: '{kind}: готово',
  readyGeneric: 'Готово',
  confirming: 'Потвърждаване на запазването',
  steps: 'Стъпки',
  outcomeUnknown: 'Възможно е работата да е приключила, но това не можа да бъде потвърдено.',
  checkAgain: 'Провери отново',
  stopHere: 'Спри тук',
  budgetPaused:
    'На пауза при лимита на бюджета: {spent} от {limit}. Последната работеща версия остава като чернова.',
  continueWith: 'Продължи с още {amount}',
  failed: 'Работата спря, преди да е готова.',
  stopped: 'Спряно. Последната запазена версия е непроменена.',
  stoppedNeutral: 'Спряно.',
  blockedApproval: 'Изчаква твоето одобрение',
  blockedBudget: 'На пауза при лимита на бюджета за тази задача',
  blockedProvider: 'Услугата на модела не отговаря в момента',
  blockedViewer: 'Трябва да се настрои кой да изпълнява това за преглеждащите',
  blockedOther: 'На пауза',
  denied: 'Нямаш достъп до тази задача.',
  loading: 'Стартиране…',
  shaping: 'Оформяне според твоята заявка…',
  pendingValue: 'Все още не е попълнено',
  unavailableValue: 'Не е налично',
  publishing: 'Публикуване…',
  fieldsCount: '{n} полета',
  busyNow: '{model} е зает в момента.',
  busyHeld: '{model} е зает.',
  busyStill: '{model} все още е зает.',
  draftSaved: 'Черновата ти е запазена.',
  retryIn: 'Нов опит след {n} s.',
  retryingNow: 'Нов опит сега.',
  checkingLastStep: 'Проверява се дали последната стъпка е завършила…',
  continueFrom: 'Ще продължи от стъпката „{step}“, когато {model} се освободи.',
  tryLater: 'Опитай отново след няколко минути.',
  stoppedAt: 'Спряно. Черновата ти е запазена на стъпката „{step}“.',
  tryNow: 'Опитай сега',
  tryAgain: 'Опитай отново',
  tryWith: 'Опитай с {model}',
  altPolicy: 'Настройките на компанията ти не позволяват друг модел.',
  altRegion: 'Друг модел не е наличен в региона за обработка на компанията ти.',
  altBudget: 'Друг модел би надхвърлил бюджета на компанията ти.',
  altNotReady: 'Друг модел все още не е готов за използване.',
  buildingWith: 'Изгражда се с {model}',
  buildingVersion: 'Изгражда се v{n} с {model}',
  versionKept: 'v{kept} остава непроменена.',
  compareWith: 'Сравни с v{n}',
  builderNotReady: 'Този вид артефакт все още не може да се изгражда в това работно пространство.',
  builderNotReadyState: 'Още не е готово',
  builderNothingStarted: 'Не беше стартирано или таксувано изграждане.',
  builderNotAllowed: 'Настройките на компанията ти не позволяват изграждане на този вид артефакт.',
  builderNotInRegion:
    'Този вид артефакт не може да се изгражда в региона за обработка на компанията ти.',
  builderOverBudget: 'Изграждането на този вид артефакт би надхвърлило бюджета на компанията ти.',
  buildLargerTakesMinutes: 'По-големите изграждания отнемат няколко минути.',
  startBlankChat: 'Започни от празен чат',
  elapsedMinSec: '{min} мин {s} с',
};

const cs: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'Čekání na spuštění',
  phaseAcquiring: 'Příprava pracovního prostoru',
  phaseAuthoring: 'Psaní',
  phaseCompiling: 'Sestavování',
  phaseRendering: 'Vytváření náhledů',
  phaseChecking: 'Ověřování funkčnosti',
  phaseSaving: 'Ukládání',
  phaseSettled: 'Hotovo',
  stepOf: '{n} z {total}',
  stillWorking: 'Stále probíhá krok: {step}',
  slowReason: 'Krok {step} trvá déle než obvykle.',
  keepWaiting: 'Čekat dál',
  stop: 'Zastavit',
  stopping: 'Zastavování…',
  continue: 'Pokračovat',
  fix: 'Opravit',
  fixInChat: 'Opravit v chatu',
  remoteWorking: '{agent} pracuje',
  remoteWorkingUnnamed: 'Připojený agent pracuje',
  remoteLastSecond: 'Poslední krok před {seconds} s',
  remoteLastMinute: 'Poslední krok před {minutes} min',
  remoteNoStep: 'Zatím nebyl nahlášen žádný krok',
  builtWith: 'Vytvořeno pomocí {model}',
  buildStartedChip: 'Vytváření zahájeno',
  approvedBuilding: 'Schváleno · Vytváří se…',
  built: 'Vytvořeno',
  byAgent: 'přes {agent}',
  agentNameReported: 'Jméno uvedené agentem',
  publishedBy: 'Publikováno agentem {agent}',
  elapsed: 'Dosavadní čas',
  ready: '{kind}: hotovo',
  readyGeneric: 'Připraveno',
  confirming: 'Potvrzování uložení',
  steps: 'Kroky',
  outcomeUnknown: 'Práce možná skončila, ale nepodařilo se to potvrdit.',
  checkAgain: 'Zkontrolovat znovu',
  stopHere: 'Zastavit zde',
  budgetPaused:
    'Pozastaveno na hranici rozpočtu: {spent} z {limit}. Poslední funkční verze zůstává konceptem.',
  continueWith: 'Pokračovat s navýšením o {amount}',
  failed: 'Práce se zastavila před dokončením.',
  stopped: 'Zastaveno. Poslední uložená verze se nezměnila.',
  stoppedNeutral: 'Zastaveno.',
  blockedApproval: 'Čeká na váš souhlas',
  blockedBudget: 'Pozastaveno na hranici rozpočtu této úlohy',
  blockedProvider: 'Služba modelu nyní neodpovídá',
  blockedViewer: 'Je třeba nastavit osobu, která to bude spouštět pro čtenáře',
  blockedOther: 'Pozastaveno',
  denied: 'K této úloze nemáte přístup.',
  loading: 'Spouštění…',
  shaping: 'Dostává podobu podle vašeho požadavku…',
  pendingValue: 'Zatím nevyplněno',
  unavailableValue: 'Není k dispozici',
  publishing: 'Publikování…',
  fieldsCount: 'Pole: {n}',
  busyNow: '{model} je právě vytížený.',
  busyHeld: '{model} je vytížený.',
  busyStill: '{model} je stále vytížený.',
  draftSaved: 'Váš koncept je uložen.',
  retryIn: 'Další pokus za {n} s.',
  retryingNow: 'Další pokus právě probíhá.',
  checkingLastStep: 'Kontroluje se, zda poslední krok skončil…',
  continueFrom: 'Až bude {model} volný, bude se pokračovat krokem „{step}“.',
  tryLater: 'Zkuste to znovu za pár minut.',
  stoppedAt: 'Zastaveno. Váš koncept je uložen u kroku „{step}“.',
  tryNow: 'Zkusit teď',
  tryAgain: 'Zkusit znovu',
  tryWith: 'Zkusit s {model}',
  altPolicy: 'Nastavení vaší společnosti jiný model nepovoluje.',
  altRegion: 'Jiný model není v regionu zpracování vaší společnosti k dispozici.',
  altBudget: 'Jiný model by překročil rozpočet vaší společnosti.',
  altNotReady: 'Jiný model zatím není připraven k použití.',
  buildingWith: 'Vytváří se s {model}',
  buildingVersion: 'Vytváří se v{n} s {model}',
  versionKept: 'v{kept} zůstává beze změny.',
  compareWith: 'Porovnat s v{n}',
  builderNotReady: 'Tento druh artefaktu zatím v tomto pracovním prostoru nelze vytvořit.',
  builderNotReadyState: 'Zatím není připraveno',
  builderNothingStarted: 'Žádné vytváření nebylo spuštěno ani účtováno.',
  builderNotAllowed: 'Nastavení vaší společnosti nepovoluje vytváření tohoto druhu artefaktu.',
  builderNotInRegion: 'Tento druh artefaktu nelze vytvořit v regionu zpracování vaší společnosti.',
  builderOverBudget: 'Vytvoření tohoto druhu artefaktu by překročilo rozpočet vaší společnosti.',
  buildLargerTakesMinutes: 'Vytvoření větších artefaktů trvá několik minut.',
  startBlankChat: 'Začít z prázdného chatu',
  elapsedMinSec: '{min} min {s} s',
};

const da: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'Venter på at starte',
  phaseAcquiring: 'Gør et arbejdsområde klar',
  phaseAuthoring: 'Skriver',
  phaseCompiling: 'Sætter det sammen',
  phaseRendering: 'Laver forhåndsvisninger',
  phaseChecking: 'Kontrollerer, at det virker',
  phaseSaving: 'Gemmer',
  phaseSettled: 'Færdig',
  stepOf: '{n} af {total}',
  stillWorking: 'Arbejder stadig på {step}',
  slowReason: '{step} tager længere tid end normalt.',
  keepWaiting: 'Vent videre',
  stop: 'Stop',
  stopping: 'Stopper…',
  continue: 'Fortsæt',
  fix: 'Ret det',
  fixInChat: 'Ret i chatten',
  remoteWorking: '{agent} arbejder',
  remoteWorkingUnnamed: 'Den tilsluttede agent arbejder',
  remoteLastSecond: 'Seneste trin for {seconds} s siden',
  remoteLastMinute: 'Seneste trin for {minutes} min siden',
  remoteNoStep: 'Ingen trin rapporteret endnu',
  builtWith: 'Bygget med {model}',
  buildStartedChip: 'Bygning startet',
  approvedBuilding: 'Godkendt · Bygger…',
  built: 'Bygget',
  byAgent: 'via {agent}',
  agentNameReported: 'Navn oplyst af agenten',
  publishedBy: 'Udgivet af {agent}',
  elapsed: 'Tid indtil nu',
  ready: '{kind} er klar',
  readyGeneric: 'Klar',
  confirming: 'Bekræfter, at det er gemt',
  steps: 'Trin',
  outcomeUnknown: 'Det er måske færdigt, men det kunne ikke bekræftes.',
  checkAgain: 'Kontrollér igen',
  stopHere: 'Stop her',
  budgetPaused:
    'Sat på pause ved budgetgrænsen: {spent} af {limit}. Den seneste fungerende version bevares som kladde.',
  continueWith: 'Fortsæt med {amount} mere',
  failed: 'Det stoppede, før det var færdigt.',
  stopped: 'Stoppet. Den senest gemte version er uændret.',
  stoppedNeutral: 'Stoppet.',
  blockedApproval: 'Venter på din godkendelse',
  blockedBudget: 'Sat på pause ved budgetgrænsen for denne oprettelse',
  blockedProvider: 'Modeltjenesten svarer ikke lige nu',
  blockedViewer: 'Nogen skal sættes op til at køre dette for dem, der ser det',
  blockedOther: 'Sat på pause',
  denied: 'Du har ikke adgang til denne oprettelse.',
  loading: 'Starter…',
  shaping: 'Former det ud fra din anmodning…',
  pendingValue: 'Ikke udfyldt endnu',
  unavailableValue: 'Ikke tilgængelig',
  publishing: 'Udgiver…',
  fieldsCount: '{n} felter',
  busyNow: '{model} er optaget lige nu.',
  busyHeld: '{model} er optaget.',
  busyStill: '{model} er stadig optaget.',
  draftSaved: 'Dit udkast er gemt.',
  retryIn: 'Prøver igen om {n} s.',
  retryingNow: 'Prøver igen nu.',
  checkingLastStep: 'Tjekker, om det sidste trin blev færdigt…',
  continueFrom: 'Det fortsætter fra trinnet “{step}”, når {model} er ledig.',
  tryLater: 'Prøv igen om et par minutter.',
  stoppedAt: 'Stoppet. Dit udkast er gemt ved trinnet “{step}”.',
  tryNow: 'Prøv nu',
  tryAgain: 'Prøv igen',
  tryWith: 'Prøv med {model}',
  altPolicy: 'Din virksomheds indstillinger tillader ikke en anden model.',
  altRegion: 'En anden model er ikke tilgængelig i din virksomheds behandlingsregion.',
  altBudget: 'En anden model ville overskride din virksomheds budget.',
  altNotReady: 'En anden model er ikke klar til brug endnu.',
  buildingWith: 'Bygger med {model}',
  buildingVersion: 'Bygger v{n} med {model}',
  versionKept: 'v{kept} forbliver, som den er.',
  compareWith: 'Sammenlign med v{n}',
  builderNotReady: 'Denne type artefakt kan ikke bygges i dette arbejdsområde endnu.',
  builderNotReadyState: 'Ikke klar endnu',
  builderNothingStarted: 'Ingen bygning blev startet eller opkrævet.',
  builderNotAllowed: 'Din virksomheds indstillinger tillader ikke bygning af denne type artefakt.',
  builderNotInRegion: 'Denne type artefakt kan ikke bygges i din virksomheds behandlingsregion.',
  builderOverBudget: 'At bygge denne type artefakt ville overskride din virksomheds budget.',
  buildLargerTakesMinutes: 'Større artefakter tager et par minutter at bygge.',
  startBlankChat: 'Start fra en tom chat',
  elapsedMinSec: '{min} min. {s} sek.',
};

const de: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'Wartet auf den Start',
  phaseAcquiring: 'Arbeitsbereich wird vorbereitet',
  phaseAuthoring: 'Wird geschrieben',
  phaseCompiling: 'Wird zusammengestellt',
  phaseRendering: 'Vorschauen werden erstellt',
  phaseChecking: 'Funktion wird geprüft',
  phaseSaving: 'Wird gespeichert',
  phaseSettled: 'Fertig',
  stepOf: '{n} von {total}',
  stillWorking: '{step} wird noch bearbeitet',
  slowReason: '{step} dauert länger als üblich.',
  keepWaiting: 'Weiter warten',
  stop: 'Stoppen',
  stopping: 'Wird gestoppt…',
  continue: 'Fortsetzen',
  fix: 'Beheben',
  fixInChat: 'Im Chat beheben',
  remoteWorking: '{agent} arbeitet',
  remoteWorkingUnnamed: 'Der verbundene Agent arbeitet',
  remoteLastSecond: 'Letzter Schritt vor {seconds} s',
  remoteLastMinute: 'Letzter Schritt vor {minutes} Min.',
  remoteNoStep: 'Noch kein Schritt gemeldet',
  builtWith: 'Erstellt mit {model}',
  buildStartedChip: 'Erstellung gestartet',
  approvedBuilding: 'Genehmigt · Wird erstellt…',
  built: 'Erstellt',
  byAgent: 'über {agent}',
  agentNameReported: 'Vom Agenten gemeldeter Name',
  publishedBy: 'Veröffentlicht von {agent}',
  elapsed: 'Bisherige Dauer',
  ready: '{kind} ist bereit',
  readyGeneric: 'Bereit',
  confirming: 'Speicherung wird bestätigt',
  steps: 'Schritte',
  outcomeUnknown:
    'Der Vorgang ist möglicherweise abgeschlossen, konnte aber nicht bestätigt werden.',
  checkAgain: 'Erneut prüfen',
  stopHere: 'Hier stoppen',
  budgetPaused:
    'Am Budgetlimit pausiert: {spent} von {limit}. Die letzte funktionierende Version bleibt als Entwurf erhalten.',
  continueWith: 'Mit weiteren {amount} fortsetzen',
  failed: 'Der Vorgang wurde vor der Fertigstellung gestoppt.',
  stopped: 'Gestoppt. Die zuletzt gespeicherte Version ist unverändert.',
  stoppedNeutral: 'Gestoppt.',
  blockedApproval: 'Warten auf Ihre Bestätigung',
  blockedBudget: 'Am Budgetlimit dieser Erstellung pausiert',
  blockedProvider: 'Der Modelldienst antwortet gerade nicht',
  blockedViewer: 'Es muss jemand eingerichtet werden, der dies für Betrachter ausführt',
  blockedOther: 'Pausiert',
  denied: 'Sie haben keinen Zugriff auf diesen Build.',
  loading: 'Wird gestartet…',
  shaping: 'Wird anhand Ihrer Anfrage erstellt…',
  pendingValue: 'Noch nicht ausgefüllt',
  unavailableValue: 'Nicht verfügbar',
  publishing: 'Wird veröffentlicht…',
  fieldsCount: '{n} Felder',
  busyNow: '{model} ist gerade ausgelastet.',
  busyHeld: '{model} ist ausgelastet.',
  busyStill: '{model} ist immer noch ausgelastet.',
  draftSaved: 'Ihr Entwurf wurde gespeichert.',
  retryIn: 'Neuer Versuch in {n} s.',
  retryingNow: 'Neuer Versuch läuft.',
  checkingLastStep: 'Es wird geprüft, ob der letzte Schritt fertig wurde…',
  continueFrom: 'Sobald {model} frei ist, geht es mit dem Schritt „{step}“ weiter.',
  tryLater: 'Versuch es in ein paar Minuten noch einmal.',
  stoppedAt: 'Gestoppt. Ihr Entwurf wurde bei „{step}“ gespeichert.',
  tryNow: 'Jetzt versuchen',
  tryAgain: 'Erneut versuchen',
  tryWith: 'Mit {model} versuchen',
  altPolicy: 'Ein anderes Modell ist aufgrund der Einstellungen Ihres Unternehmens nicht zulässig.',
  altRegion:
    'Ein anderes Modell ist in der Verarbeitungsregion Ihres Unternehmens nicht verfügbar.',
  altBudget: 'Ein anderes Modell würde das Budget Ihres Unternehmens überschreiten.',
  altNotReady: 'Ein anderes Modell ist noch nicht einsatzbereit.',
  buildingWith: 'Wird mit {model} erstellt',
  buildingVersion: 'v{n} wird mit {model} erstellt',
  versionKept: 'v{kept} bleibt unverändert.',
  compareWith: 'Mit v{n} vergleichen',
  builderNotReady:
    'Diese Art von Artefakt kann in diesem Arbeitsbereich noch nicht erstellt werden.',
  builderNotReadyState: 'Noch nicht bereit',
  builderNothingStarted: 'Es wurde keine Erstellung gestartet oder berechnet.',
  builderNotAllowed:
    'Die Einstellungen Ihres Unternehmens erlauben nicht die Erstellung dieser Art von Artefakt.',
  builderNotInRegion:
    'Diese Art von Artefakt kann in der Verarbeitungsregion Ihres Unternehmens nicht erstellt werden.',
  builderOverBudget:
    'Die Erstellung dieser Art von Artefakt würde das Budget Ihres Unternehmens überschreiten.',
  buildLargerTakesMinutes: 'Größere Artefakte zu erstellen dauert ein paar Minuten.',
  startBlankChat: 'Mit einem leeren Chat beginnen',
  elapsedMinSec: '{min} Min. {s} Sek.',
};

const el: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'Αναμονή για έναρξη',
  phaseAcquiring: 'Προετοιμασία χώρου εργασίας',
  phaseAuthoring: 'Σύνταξη',
  phaseCompiling: 'Συναρμολόγηση',
  phaseRendering: 'Δημιουργία προεπισκοπήσεων',
  phaseChecking: 'Έλεγχος λειτουργίας',
  phaseSaving: 'Αποθήκευση',
  phaseSettled: 'Ολοκληρώθηκε',
  stepOf: '{n} από {total}',
  stillWorking: 'Το βήμα {step} συνεχίζεται',
  slowReason: 'Το βήμα {step} διαρκεί περισσότερο από το συνηθισμένο.',
  keepWaiting: 'Συνέχιση αναμονής',
  stop: 'Διακοπή',
  stopping: 'Γίνεται διακοπή…',
  continue: 'Συνέχεια',
  fix: 'Διόρθωση',
  fixInChat: 'Διόρθωση στη συνομιλία',
  remoteWorking: 'Ο πράκτορας {agent} εργάζεται',
  remoteWorkingUnnamed: 'Ο συνδεδεμένος πράκτορας εργάζεται',
  remoteLastSecond: 'Τελευταίο βήμα πριν από {seconds} δευτ.',
  remoteLastMinute: 'Τελευταίο βήμα πριν από {minutes} λεπτά',
  remoteNoStep: 'Δεν έχει αναφερθεί ακόμη κανένα βήμα',
  builtWith: 'Δημιουργήθηκε με {model}',
  buildStartedChip: 'Η κατασκευή ξεκίνησε',
  approvedBuilding: 'Εγκρίθηκε · Δημιουργείται…',
  built: 'Δημιουργήθηκε',
  byAgent: 'μέσω {agent}',
  agentNameReported: 'Όνομα που ανέφερε ο πράκτορας',
  publishedBy: 'Δημοσιεύτηκε από {agent}',
  elapsed: 'Χρόνος που πέρασε',
  ready: '{kind}: έτοιμο',
  readyGeneric: 'Έτοιμο',
  confirming: 'Επιβεβαίωση αποθήκευσης',
  steps: 'Βήματα',
  outcomeUnknown: 'Μπορεί να ολοκληρώθηκε, αλλά δεν ήταν δυνατή η επιβεβαίωση.',
  checkAgain: 'Έλεγχος ξανά',
  stopHere: 'Διακοπή εδώ',
  budgetPaused:
    'Σε παύση στο όριο προϋπολογισμού: {spent} από {limit}. Η τελευταία λειτουργική έκδοση παραμένει ως πρόχειρο.',
  continueWith: 'Συνέχεια με επιπλέον {amount}',
  failed: 'Σταμάτησε πριν ολοκληρωθεί.',
  stopped: 'Διακόπηκε. Η τελευταία αποθηκευμένη έκδοση παραμένει αμετάβλητη.',
  stoppedNeutral: 'Διακόπηκε.',
  blockedApproval: 'Αναμονή για την έγκρισή σας',
  blockedBudget: 'Σε παύση στο όριο προϋπολογισμού αυτής της δημιουργίας',
  blockedProvider: 'Η υπηρεσία του μοντέλου δεν απαντά αυτή τη στιγμή',
  blockedViewer: 'Πρέπει να οριστεί κάποιος που θα το εκτελεί για τους θεατές',
  blockedOther: 'Σε παύση',
  denied: 'Δεν έχετε πρόσβαση σε αυτή τη δημιουργία.',
  loading: 'Έναρξη…',
  shaping: 'Διαμόρφωση βάσει του αιτήματός σας…',
  pendingValue: 'Δεν έχει συμπληρωθεί ακόμη',
  unavailableValue: 'Μη διαθέσιμο',
  publishing: 'Δημοσίευση…',
  fieldsCount: '{n} πεδία',
  busyNow: 'Το {model} είναι απασχολημένο αυτή τη στιγμή.',
  busyHeld: 'Το {model} είναι απασχολημένο.',
  busyStill: 'Το {model} είναι ακόμη απασχολημένο.',
  draftSaved: 'Το πρόχειρό σας έχει αποθηκευτεί.',
  retryIn: 'Νέα προσπάθεια σε {n} s.',
  retryingNow: 'Νέα προσπάθεια τώρα.',
  checkingLastStep: 'Ελέγχεται αν ολοκληρώθηκε το τελευταίο βήμα…',
  continueFrom: 'Θα συνεχίσει από το βήμα «{step}» όταν το {model} είναι διαθέσιμο.',
  tryLater: 'Δοκιμάστε ξανά σε λίγα λεπτά.',
  stoppedAt: 'Διακόπηκε. Το πρόχειρό σας έχει αποθηκευτεί στο βήμα «{step}».',
  tryNow: 'Δοκιμή τώρα',
  tryAgain: 'Δοκιμή ξανά',
  tryWith: 'Δοκιμή με {model}',
  altPolicy: 'Οι ρυθμίσεις της εταιρείας σας δεν επιτρέπουν άλλο μοντέλο.',
  altRegion: 'Άλλο μοντέλο δεν είναι διαθέσιμο στην περιοχή επεξεργασίας της εταιρείας σας.',
  altBudget: 'Άλλο μοντέλο θα υπερέβαινε τον προϋπολογισμό της εταιρείας σας.',
  altNotReady: 'Άλλο μοντέλο δεν είναι ακόμη έτοιμο για χρήση.',
  buildingWith: 'Δημιουργία με {model}',
  buildingVersion: 'Δημιουργία v{n} με {model}',
  versionKept: 'Η v{kept} παραμένει ως έχει.',
  compareWith: 'Σύγκριση με v{n}',
  builderNotReady:
    'Αυτό το είδος δημιουργήματος δεν μπορεί ακόμη να κατασκευαστεί σε αυτόν τον χώρο εργασίας.',
  builderNotReadyState: 'Δεν είναι ακόμη έτοιμο',
  builderNothingStarted: 'Δεν ξεκίνησε ούτε χρεώθηκε καμία κατασκευή.',
  builderNotAllowed:
    'Οι ρυθμίσεις της εταιρείας σας δεν επιτρέπουν την κατασκευή αυτού του είδους δημιουργήματος.',
  builderNotInRegion:
    'Αυτό το είδος δημιουργήματος δεν μπορεί να κατασκευαστεί στην περιοχή επεξεργασίας της εταιρείας σας.',
  builderOverBudget:
    'Η κατασκευή αυτού του είδους δημιουργήματος θα υπερέβαινε τον προϋπολογισμό της εταιρείας σας.',
  buildLargerTakesMinutes: 'Οι μεγαλύτερες κατασκευές διαρκούν λίγα λεπτά.',
  startBlankChat: 'Ξεκινήστε από κενή συνομιλία',
  elapsedMinSec: '{min} λεπ. {s} δευτ.',
};

const es: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'Esperando para empezar',
  phaseAcquiring: 'Preparando un espacio de trabajo',
  phaseAuthoring: 'Escribiendo',
  phaseCompiling: 'Ensamblando',
  phaseRendering: 'Creando vistas previas',
  phaseChecking: 'Comprobando que funciona',
  phaseSaving: 'Guardando',
  phaseSettled: 'Terminado',
  stepOf: '{n} de {total}',
  stillWorking: 'Todavía trabajando en {step}',
  slowReason: '{step} está tardando más de lo habitual.',
  keepWaiting: 'Seguir esperando',
  stop: 'Detener',
  stopping: 'Deteniendo…',
  continue: 'Continuar',
  fix: 'Corregir',
  fixInChat: 'Corregir en el chat',
  remoteWorking: '{agent} está trabajando',
  remoteWorkingUnnamed: 'El agente conectado está trabajando',
  remoteLastSecond: 'Último paso hace {seconds} s',
  remoteLastMinute: 'Último paso hace {minutes} min',
  remoteNoStep: 'Todavía no se ha comunicado ningún paso',
  builtWith: 'Creado con {model}',
  buildStartedChip: 'Creación iniciada',
  approvedBuilding: 'Aprobado · Creando…',
  built: 'Creado',
  byAgent: 'mediante {agent}',
  agentNameReported: 'Nombre comunicado por el agente',
  publishedBy: 'Publicado por {agent}',
  elapsed: 'Tiempo transcurrido',
  ready: '{kind}: listo',
  readyGeneric: 'Listo',
  confirming: 'Confirmando que se ha guardado',
  steps: 'Pasos',
  outcomeUnknown: 'Puede que haya terminado, pero no se ha podido confirmar.',
  checkAgain: 'Comprobar de nuevo',
  stopHere: 'Detener aquí',
  budgetPaused:
    'En pausa por el límite del presupuesto: {spent} de {limit}. La última versión que funciona se conserva como borrador.',
  continueWith: 'Continuar con {amount} más',
  failed: 'Se ha detenido antes de terminar.',
  stopped: 'Detenido. La última versión guardada no ha cambiado.',
  stoppedNeutral: 'Detenido.',
  blockedApproval: 'Esperando tu aprobación',
  blockedBudget: 'En pausa por el presupuesto de esta creación',
  blockedProvider: 'El servicio del modelo no responde en este momento',
  blockedViewer: 'Hay que configurar a alguien para ejecutarlo para los lectores',
  blockedOther: 'En pausa',
  denied: 'No tienes acceso a esta creación.',
  loading: 'Iniciando…',
  shaping: 'Dándole forma a partir de tu solicitud…',
  pendingValue: 'Todavía sin completar',
  unavailableValue: 'No disponible',
  publishing: 'Publicando…',
  fieldsCount: '{n} campos',
  busyNow: '{model} está ocupado en este momento.',
  busyHeld: '{model} está ocupado.',
  busyStill: '{model} sigue ocupado.',
  draftSaved: 'Tu borrador está guardado.',
  retryIn: 'Se volverá a intentar en {n} s.',
  retryingNow: 'Volviendo a intentarlo ahora.',
  checkingLastStep: 'Comprobando si terminó el último paso…',
  continueFrom: 'Continuará desde el paso «{step}» cuando {model} esté libre.',
  tryLater: 'Vuelve a intentarlo en unos minutos.',
  stoppedAt: 'Detenido. Tu borrador está guardado en el paso «{step}».',
  tryNow: 'Intentar ahora',
  tryAgain: 'Volver a intentar',
  tryWith: 'Probar con {model}',
  altPolicy: 'La configuración de tu empresa no permite otro modelo.',
  altRegion: 'Otro modelo no está disponible en la región de procesamiento de tu empresa.',
  altBudget: 'Otro modelo superaría el presupuesto de tu empresa.',
  altNotReady: 'Otro modelo aún no está listo para usarse.',
  buildingWith: 'Creando con {model}',
  buildingVersion: 'Creando v{n} con {model}',
  versionKept: 'v{kept} se queda como está.',
  compareWith: 'Comparar con v{n}',
  builderNotReady: 'Este tipo de artefacto aún no se puede crear en este espacio de trabajo.',
  builderNotReadyState: 'Aún no está listo',
  builderNothingStarted: 'No se inició ni se cobró ninguna creación.',
  builderNotAllowed: 'La configuración de tu empresa no permite crear este tipo de artefacto.',
  builderNotInRegion:
    'Este tipo de artefacto no se puede crear en la región de procesamiento de tu empresa.',
  builderOverBudget: 'Crear este tipo de artefacto superaría el presupuesto de tu empresa.',
  buildLargerTakesMinutes: 'Los artefactos más grandes tardan unos minutos en crearse.',
  startBlankChat: 'Empezar desde un chat en blanco',
  elapsedMinSec: '{min} min {s} s',
};

const et: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'Ootab käivitamist',
  phaseAcquiring: 'Tööruumi ettevalmistamine',
  phaseAuthoring: 'Kirjutamine',
  phaseCompiling: 'Kokkupanemine',
  phaseRendering: 'Eelvaadete loomine',
  phaseChecking: 'Toimimise kontrollimine',
  phaseSaving: 'Salvestamine',
  phaseSettled: 'Valmis',
  stepOf: '{n} / {total}',
  stillWorking: 'Etapp {step} on veel pooleli',
  slowReason: 'Etapp {step} võtab tavalisest kauem aega.',
  keepWaiting: 'Oota edasi',
  stop: 'Peata',
  stopping: 'Peatamine…',
  continue: 'Jätka',
  fix: 'Paranda',
  fixInChat: 'Paranda vestluses',
  remoteWorking: '{agent} töötab',
  remoteWorkingUnnamed: 'Ühendatud agent töötab',
  remoteLastSecond: 'Viimane etapp {seconds} s tagasi',
  remoteLastMinute: 'Viimane etapp {minutes} min tagasi',
  remoteNoStep: 'Ühtegi etappi pole veel teatatud',
  builtWith: 'Loodud mudeliga {model}',
  buildStartedChip: 'Ehitamine alustatud',
  approvedBuilding: 'Kinnitatud · Luuakse…',
  built: 'Loodud',
  byAgent: 'agendi {agent} kaudu',
  agentNameReported: 'Agendi teatatud nimi',
  publishedBy: 'Avaldaja: {agent}',
  elapsed: 'Kulunud aeg',
  ready: '{kind} on valmis',
  readyGeneric: 'Valmis',
  confirming: 'Salvestamise kinnitamine',
  steps: 'Etapid',
  outcomeUnknown: 'Töö võib olla valmis, kuid seda ei õnnestunud kinnitada.',
  checkAgain: 'Kontrolli uuesti',
  stopHere: 'Peata siin',
  budgetPaused:
    'Peatatud eelarvepiiril: {spent} / {limit}. Viimane toimiv versioon jääb mustandiks.',
  continueWith: 'Jätka, lisades eelarvesse {amount}',
  failed: 'Töö peatus enne valmimist.',
  stopped: 'Peatatud. Viimane salvestatud versioon on muutmata.',
  stoppedNeutral: 'Peatatud.',
  blockedApproval: 'Ootab sinu heakskiitu',
  blockedBudget: 'Peatatud selle töö eelarvepiiril',
  blockedProvider: 'Mudeliteenus ei vasta praegu',
  blockedViewer: 'Tuleb määrata keegi, kes seda vaatajate jaoks käitab',
  blockedOther: 'Pausil',
  denied: 'Sul pole sellele tööle juurdepääsu.',
  loading: 'Käivitamine…',
  shaping: 'Kujundamine sinu soovi põhjal…',
  pendingValue: 'Veel täitmata',
  unavailableValue: 'Pole saadaval',
  publishing: 'Avaldamine…',
  fieldsCount: '{n} välja',
  busyNow: '{model} on praegu hõivatud.',
  busyHeld: '{model} on hõivatud.',
  busyStill: '{model} on endiselt hõivatud.',
  draftSaved: 'Sinu mustand on salvestatud.',
  retryIn: 'Uus katse {n} s pärast.',
  retryingNow: 'Uus katse käib.',
  checkingLastStep: 'Kontrollitakse, kas viimane samm lõppes…',
  continueFrom: 'Kui {model} vabaneb, jätkatakse sammust „{step}”.',
  tryLater: 'Proovi mõne minuti pärast uuesti.',
  stoppedAt: 'Peatatud. Sinu mustand on salvestatud sammu „{step}” juures.',
  tryNow: 'Proovi kohe',
  tryAgain: 'Proovi uuesti',
  tryWith: 'Proovi mudeliga {model}',
  altPolicy: 'Sinu ettevõtte seaded ei luba teist mudelit.',
  altRegion: 'Teine mudel pole sinu ettevõtte töötlemispiirkonnas saadaval.',
  altBudget: 'Teine mudel ületaks sinu ettevõtte eelarve.',
  altNotReady: 'Teine mudel pole veel kasutamiseks valmis.',
  buildingWith: 'Ehitatakse mudeliga {model}',
  buildingVersion: 'v{n} ehitatakse mudeliga {model}',
  versionKept: 'v{kept} jääb samaks.',
  compareWith: 'Võrdle versiooniga v{n}',
  builderNotReady: 'Seda tüüpi artefakti ei saa selles tööruumis veel ehitada.',
  builderNotReadyState: 'Pole veel valmis',
  builderNothingStarted: 'Ühtegi ehitamist ei käivitatud ega arveldatud.',
  builderNotAllowed: 'Sinu ettevõtte seaded ei luba seda tüüpi artefakti ehitada.',
  builderNotInRegion: 'Seda tüüpi artefakti ei saa sinu ettevõtte töötlemispiirkonnas ehitada.',
  builderOverBudget: 'Seda tüüpi artefakti ehitamine ületaks sinu ettevõtte eelarve.',
  buildLargerTakesMinutes: 'Suuremate artefaktide ehitamine võtab mõne minuti.',
  startBlankChat: 'Alusta tühjast vestlusest',
  elapsedMinSec: '{min} min {s} s',
};

const fi: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'Odotetaan aloitusta',
  phaseAcquiring: 'Valmistellaan työtilaa',
  phaseAuthoring: 'Kirjoitetaan',
  phaseCompiling: 'Kootaan',
  phaseRendering: 'Luodaan esikatseluja',
  phaseChecking: 'Tarkistetaan toimivuus',
  phaseSaving: 'Tallennetaan',
  phaseSettled: 'Valmis',
  stepOf: '{n}/{total}',
  stillWorking: 'Vaihe {step} on yhä kesken',
  slowReason: 'Vaihe {step} kestää tavallista kauemmin.',
  keepWaiting: 'Jatka odottamista',
  stop: 'Pysäytä',
  stopping: 'Pysäytetään…',
  continue: 'Jatka',
  fix: 'Korjaa',
  fixInChat: 'Korjaa keskustelussa',
  remoteWorking: '{agent} työskentelee',
  remoteWorkingUnnamed: 'Yhdistetty agentti työskentelee',
  remoteLastSecond: 'Viimeisin vaihe {seconds} s sitten',
  remoteLastMinute: 'Viimeisin vaihe {minutes} min sitten',
  remoteNoStep: 'Vaiheita ei ole vielä raportoitu',
  builtWith: 'Luotu mallilla {model}',
  buildStartedChip: 'Rakentaminen aloitettu',
  approvedBuilding: 'Hyväksytty · Luodaan…',
  built: 'Luotu',
  byAgent: 'agentin {agent} kautta',
  agentNameReported: 'Agentin ilmoittama nimi',
  publishedBy: 'Julkaisija: {agent}',
  elapsed: 'Kulunut aika',
  ready: '{kind} on valmis',
  readyGeneric: 'Valmis',
  confirming: 'Vahvistetaan tallennus',
  steps: 'Vaiheet',
  outcomeUnknown: 'Työ on saattanut valmistua, mutta sitä ei voitu vahvistaa.',
  checkAgain: 'Tarkista uudelleen',
  stopHere: 'Pysäytä tähän',
  budgetPaused:
    'Keskeytetty budjettirajalla: {spent}/{limit}. Viimeisin toimiva versio säilyy luonnoksena.',
  continueWith: 'Jatka lisäämällä {amount}',
  failed: 'Työ pysähtyi ennen valmistumistaan.',
  stopped: 'Pysäytetty. Viimeksi tallennettu versio on ennallaan.',
  stoppedNeutral: 'Pysäytetty.',
  blockedApproval: 'Odotetaan hyväksyntääsi',
  blockedBudget: 'Keskeytetty tämän työn budjettirajalla',
  blockedProvider: 'Mallipalvelu ei vastaa juuri nyt',
  blockedViewer: 'Katselijoita varten on määritettävä joku suorittamaan tämä',
  blockedOther: 'Keskeytetty',
  denied: 'Sinulla ei ole pääsyä tähän työhön.',
  loading: 'Käynnistetään…',
  shaping: 'Muotoillaan pyyntösi pohjalta…',
  pendingValue: 'Ei vielä täytetty',
  unavailableValue: 'Ei saatavilla',
  publishing: 'Julkaistaan…',
  fieldsCount: '{n} kenttää',
  busyNow: '{model} on juuri nyt varattu.',
  busyHeld: '{model} on varattu.',
  busyStill: '{model} on yhä varattu.',
  draftSaved: 'Luonnoksesi on tallennettu.',
  retryIn: 'Uusi yritys {n} s kuluttua.',
  retryingNow: 'Uusi yritys käynnissä.',
  checkingLastStep: 'Tarkistetaan, valmistuiko viimeisin vaihe…',
  continueFrom: 'Jatketaan vaiheesta ”{step}”, kun {model} vapautuu.',
  tryLater: 'Yritä uudelleen muutaman minuutin päästä.',
  stoppedAt: 'Pysäytetty. Luonnoksesi on tallennettu vaiheeseen ”{step}”.',
  tryNow: 'Yritä nyt',
  tryAgain: 'Yritä uudelleen',
  tryWith: 'Kokeile mallilla {model}',
  altPolicy: 'Yrityksesi asetukset eivät salli toista mallia.',
  altRegion: 'Toinen malli ei ole saatavilla yrityksesi käsittelyalueella.',
  altBudget: 'Toinen malli ylittäisi yrityksesi budjetin.',
  altNotReady: 'Toinen malli ei ole vielä käyttövalmis.',
  buildingWith: 'Rakennetaan mallilla {model}',
  buildingVersion: 'Rakennetaan v{n} mallilla {model}',
  versionKept: 'v{kept} pysyy ennallaan.',
  compareWith: 'Vertaa versioon v{n}',
  builderNotReady: 'Tämän tyyppistä artefaktia ei voi vielä rakentaa tässä työtilassa.',
  builderNotReadyState: 'Ei vielä valmis',
  builderNothingStarted: 'Rakentamista ei käynnistetty eikä veloitettu.',
  builderNotAllowed: 'Yrityksesi asetukset eivät salli tämän tyyppisen artefaktin rakentamista.',
  builderNotInRegion: 'Tämän tyyppistä artefaktia ei voi rakentaa yrityksesi käsittelyalueella.',
  builderOverBudget: 'Tämän tyyppisen artefaktin rakentaminen ylittäisi yrityksesi budjetin.',
  buildLargerTakesMinutes: 'Suurempien artefaktien rakentaminen kestää muutaman minuutin.',
  startBlankChat: 'Aloita tyhjästä keskustelusta',
  elapsedMinSec: '{min} min {s} s',
};

const fr: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'En attente de démarrage',
  phaseAcquiring: 'Préparation de l’espace de travail',
  phaseAuthoring: 'Rédaction',
  phaseCompiling: 'Assemblage',
  phaseRendering: 'Création des aperçus',
  phaseChecking: 'Vérification du fonctionnement',
  phaseSaving: 'Enregistrement',
  phaseSettled: 'Terminé',
  stepOf: '{n} sur {total}',
  stillWorking: 'Travail toujours en cours sur {step}',
  slowReason: '{step} prend plus de temps que d’habitude.',
  keepWaiting: 'Continuer à attendre',
  stop: 'Arrêter',
  stopping: 'Arrêt en cours…',
  continue: 'Continuer',
  fix: 'Corriger',
  fixInChat: 'Corriger dans le chat',
  remoteWorking: '{agent} travaille',
  remoteWorkingUnnamed: 'L’agent connecté travaille',
  remoteLastSecond: 'Dernière étape il y a {seconds} s',
  remoteLastMinute: 'Dernière étape il y a {minutes} min',
  remoteNoStep: 'Aucune étape signalée pour le moment',
  builtWith: 'Créé avec {model}',
  buildStartedChip: 'Création lancée',
  approvedBuilding: 'Approuvé · Création en cours…',
  built: 'Créé',
  byAgent: 'par {agent}',
  agentNameReported: 'Nom indiqué par l’agent',
  publishedBy: 'Publié par {agent}',
  elapsed: 'Temps écoulé',
  ready: '{kind} : prêt',
  readyGeneric: 'Prêt',
  confirming: 'Confirmation de l’enregistrement',
  steps: 'Étapes',
  outcomeUnknown: 'Le travail est peut-être terminé, mais cela n’a pas pu être confirmé.',
  checkAgain: 'Vérifier à nouveau',
  stopHere: 'Arrêter ici',
  budgetPaused:
    'En pause à la limite du budget : {spent} sur {limit}. La dernière version fonctionnelle reste un brouillon.',
  continueWith: 'Continuer avec {amount} de plus',
  failed: 'Le travail s’est arrêté avant d’être terminé.',
  stopped: 'Arrêté. La dernière version enregistrée reste inchangée.',
  stoppedNeutral: 'Arrêté.',
  blockedApproval: 'En attente de votre accord',
  blockedBudget: 'En pause à la limite du budget de cette création',
  blockedProvider: 'Le service du modèle ne répond pas pour le moment',
  blockedViewer: 'Une personne doit être désignée pour l’exécuter pour les lecteurs',
  blockedOther: 'En pause',
  denied: 'Vous n’avez pas accès à cette création.',
  loading: 'Démarrage…',
  shaping: 'Mise en forme à partir de votre demande…',
  pendingValue: 'Pas encore renseigné',
  unavailableValue: 'Indisponible',
  publishing: 'Publication…',
  fieldsCount: '{n} champs',
  busyNow: '{model} est occupé pour le moment.',
  busyHeld: '{model} est occupé.',
  busyStill: '{model} est toujours occupé.',
  draftSaved: 'Votre brouillon est enregistré.',
  retryIn: 'Nouvel essai dans {n} s.',
  retryingNow: 'Nouvel essai en cours.',
  checkingLastStep: 'Vérification de la fin de la dernière étape…',
  continueFrom: 'La création reprendra à l’étape « {step} » dès qu’{model} sera disponible.',
  tryLater: 'Réessayez dans quelques minutes.',
  stoppedAt: 'Arrêté. Votre brouillon est enregistré à l’étape « {step} ».',
  tryNow: 'Essayer maintenant',
  tryAgain: 'Réessayer',
  tryWith: 'Essayer avec {model}',
  altPolicy: 'Les paramètres de votre entreprise n’autorisent pas un autre modèle.',
  altRegion:
    'Un autre modèle n’est pas disponible dans la région de traitement de votre entreprise.',
  altBudget: 'Un autre modèle dépasserait le budget de votre entreprise.',
  altNotReady: 'Un autre modèle n’est pas encore prêt à être utilisé.',
  buildingWith: 'Création avec {model}',
  buildingVersion: 'Création de v{n} avec {model}',
  versionKept: 'v{kept} reste telle quelle.',
  compareWith: 'Comparer avec v{n}',
  builderNotReady: 'Ce type d’artefact ne peut pas encore être créé dans cet espace de travail.',
  builderNotReadyState: 'Pas encore prêt',
  builderNothingStarted: 'Aucune création n’a été lancée ni facturée.',
  builderNotAllowed:
    'Les paramètres de votre entreprise n’autorisent pas la création de ce type d’artefact.',
  builderNotInRegion:
    'Ce type d’artefact ne peut pas être créé dans la région de traitement de votre entreprise.',
  builderOverBudget: 'Créer ce type d’artefact dépasserait le budget de votre entreprise.',
  buildLargerTakesMinutes: 'Les artefacts plus volumineux prennent quelques minutes à créer.',
  startBlankChat: 'Partir d’un chat vierge',
  elapsedMinSec: '{min} min {s} s',
};

const ga: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'Ag fanacht le tosú',
  phaseAcquiring: 'Ag ullmhú spáis oibre',
  phaseAuthoring: 'Ag scríobh',
  phaseCompiling: 'Á chur le chéile',
  phaseRendering: 'Ag cruthú réamhamharcanna',
  phaseChecking: 'Ag seiceáil go n-oibríonn sé',
  phaseSaving: 'Á shábháil',
  phaseSettled: 'Críochnaithe',
  stepOf: '{n} as {total}',
  stillWorking: 'Ag obair ar {step} fós',
  slowReason: 'Tá {step} ag glacadh níos faide ná mar is gnách.',
  keepWaiting: 'Lean ort ag fanacht',
  stop: 'Stop',
  stopping: 'Á stopadh…',
  continue: 'Lean ar aghaidh',
  fix: 'Deisigh é',
  fixInChat: 'Deisigh sa chomhrá',
  remoteWorking: 'Tá {agent} ag obair',
  remoteWorkingUnnamed: 'Tá an gníomhaí nasctha ag obair',
  remoteLastSecond: 'An chéim dheireanach {seconds} s ó shin',
  remoteLastMinute: 'An chéim dheireanach {minutes} nóim ó shin',
  remoteNoStep: 'Níor tuairiscíodh aon chéim fós',
  builtWith: 'Cruthaithe le {model}',
  buildStartedChip: 'Tosaíodh an tógáil',
  approvedBuilding: 'Faofa · Á chruthú…',
  built: 'Cruthaithe',
  byAgent: 'trí {agent}',
  agentNameReported: 'Ainm a thuairiscigh an gníomhaí',
  publishedBy: 'Foilsithe ag {agent}',
  elapsed: 'Am caite go dtí seo',
  ready: 'Tá {kind} réidh',
  readyGeneric: 'Réidh',
  confirming: 'Ag deimhniú gur sábháladh é',
  steps: 'Céimeanna',
  outcomeUnknown: 'Seans go bhfuil sé críochnaithe, ach níorbh fhéidir é sin a dheimhniú.',
  checkAgain: 'Seiceáil arís',
  stopHere: 'Stop anseo',
  budgetPaused:
    'Ar sos ag teorainn an bhuiséid: {spent} as {limit}. Fanann an leagan oibre deireanach mar dhréacht.',
  continueWith: 'Lean ar aghaidh le {amount} sa bhreis',
  failed: 'Stop sé sula raibh sé réidh.',
  stopped: 'Stoptha. Níl aon athrú ar an leagan deireanach a sábháladh.',
  stoppedNeutral: 'Stoptha.',
  blockedApproval: 'Ag fanacht le do chead',
  blockedBudget: 'Ar sos ag teorainn bhuiséad na hoibre seo',
  blockedProvider: 'Níl seirbhís na samhla ag freagairt faoi láthair',
  blockedViewer: 'Ní mór duine a shocrú chun é seo a rith do lucht féachana',
  blockedOther: 'Ar sos',
  denied: 'Níl rochtain agat ar an obair seo.',
  loading: 'Ag tosú…',
  shaping: 'Á mhúnlú de réir d’iarratais…',
  pendingValue: 'Níl sé líonta isteach fós',
  unavailableValue: 'Níl sé ar fáil',
  publishing: 'Á fhoilsiú…',
  fieldsCount: '{n} réimse',
  busyNow: 'Tá {model} gnóthach faoi láthair.',
  busyHeld: 'Tá {model} gnóthach.',
  busyStill: 'Tá {model} fós gnóthach.',
  draftSaved: 'Tá do dhréacht sábháilte.',
  retryIn: 'Triailfear arís i gceann {n} s.',
  retryingNow: 'Ag triail arís anois.',
  checkingLastStep: 'Ag seiceáil ar críochnaíodh an chéim dheireanach…',
  continueFrom: 'Leanfar ar aghaidh ón gcéim “{step}” nuair a bheidh {model} saor.',
  tryLater: 'Bain triail eile as i gceann cúpla nóiméad.',
  stoppedAt: 'Stoptha. Tá do dhréacht sábháilte ag an gcéim “{step}”.',
  tryNow: 'Triail anois',
  tryAgain: 'Triail arís',
  tryWith: 'Bain triail as {model}',
  altPolicy: 'Ní cheadaíonn socruithe do chuideachta samhail eile.',
  altRegion: 'Níl samhail eile ar fáil i réigiún próiseála do chuideachta.',
  altBudget: 'Rachadh samhail eile thar bhuiséad do chuideachta.',
  altNotReady: 'Níl samhail eile réidh le húsáid fós.',
  buildingWith: 'Á thógáil le {model}',
  buildingVersion: 'v{n} á thógáil le {model}',
  versionKept: 'Fanfaidh v{kept} mar atá.',
  compareWith: 'Cuir i gcomparáid le v{n}',
  builderNotReady: 'Ní féidir an cineál seo déantáin a thógáil sa spás oibre seo fós.',
  builderNotReadyState: 'Níl sé réidh fós',
  builderNothingStarted: 'Níor tosaíodh tógáil ar bith agus níor gearradh táille uirthi.',
  builderNotAllowed: 'Ní cheadaíonn socruithe do chuideachta an cineál seo déantáin a thógáil.',
  builderNotInRegion:
    'Ní féidir an cineál seo déantáin a thógáil i réigiún próiseála do chuideachta.',
  builderOverBudget: 'Rachadh tógáil an chineáil seo déantáin thar bhuiséad do chuideachta.',
  buildLargerTakesMinutes: 'Tógann déantáin níos mó cúpla nóiméad.',
  startBlankChat: 'Tosaigh ó chomhrá bán',
  elapsedMinSec: '{min} nóim. {s} soic.',
};

const hr: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'Čekanje na početak',
  phaseAcquiring: 'Priprema radnog prostora',
  phaseAuthoring: 'Pisanje',
  phaseCompiling: 'Sastavljanje',
  phaseRendering: 'Izrada pretpregleda',
  phaseChecking: 'Provjera rada',
  phaseSaving: 'Spremanje',
  phaseSettled: 'Dovršeno',
  stepOf: '{n} od {total}',
  stillWorking: 'Još traje korak: {step}',
  slowReason: 'Korak {step} traje dulje nego inače.',
  keepWaiting: 'Nastavi čekati',
  stop: 'Zaustavi',
  stopping: 'Zaustavljanje…',
  continue: 'Nastavi',
  fix: 'Ispravi',
  fixInChat: 'Ispravi u razgovoru',
  remoteWorking: '{agent} radi',
  remoteWorkingUnnamed: 'Povezani agent radi',
  remoteLastSecond: 'Posljednji korak prije {seconds} s',
  remoteLastMinute: 'Posljednji korak prije {minutes} min',
  remoteNoStep: 'Još nije prijavljen nijedan korak',
  builtWith: 'Izrađeno uz {model}',
  buildStartedChip: 'Izrada je pokrenuta',
  approvedBuilding: 'Odobreno · Izrađuje se…',
  built: 'Izrađeno',
  byAgent: 'putem {agent}',
  agentNameReported: 'Ime koje je naveo agent',
  publishedBy: 'Objavio: {agent}',
  elapsed: 'Proteklo vrijeme',
  ready: '{kind}: spremno',
  readyGeneric: 'Spremno',
  confirming: 'Potvrda spremanja',
  steps: 'Koraci',
  outcomeUnknown: 'Posao je možda dovršen, ali to nije bilo moguće potvrditi.',
  checkAgain: 'Provjeri ponovno',
  stopHere: 'Zaustavi ovdje',
  budgetPaused:
    'Pauzirano na granici proračuna: {spent} od {limit}. Posljednja funkcionalna verzija ostaje skica.',
  continueWith: 'Nastavi s dodatnih {amount}',
  failed: 'Posao je stao prije dovršetka.',
  stopped: 'Zaustavljeno. Posljednja spremljena verzija nije promijenjena.',
  stoppedNeutral: 'Zaustavljeno.',
  blockedApproval: 'Čeka tvoje odobrenje',
  blockedBudget: 'Pauzirano na granici proračuna za ovu izradu',
  blockedProvider: 'Usluga modela trenutačno ne odgovara',
  blockedViewer: 'Potrebno je postaviti nekoga da ovo pokreće za gledatelje',
  blockedOther: 'Pauzirano',
  denied: 'Nemaš pristup ovoj izradi.',
  loading: 'Pokretanje…',
  shaping: 'Oblikovanje prema tvojem zahtjevu…',
  pendingValue: 'Još nije ispunjeno',
  unavailableValue: 'Nije dostupno',
  publishing: 'Objavljivanje…',
  fieldsCount: 'Polja: {n}',
  busyNow: '{model} je trenutačno zauzet.',
  busyHeld: '{model} je zauzet.',
  busyStill: '{model} je i dalje zauzet.',
  draftSaved: 'Tvoj je nacrt spremljen.',
  retryIn: 'Novi pokušaj za {n} s.',
  retryingNow: 'Novi pokušaj je u tijeku.',
  checkingLastStep: 'Provjerava se je li posljednji korak završen…',
  continueFrom: 'Nastavit će se od koraka „{step}” kad {model} bude slobodan.',
  tryLater: 'Pokušaj ponovno za nekoliko minuta.',
  stoppedAt: 'Zaustavljeno. Tvoj je nacrt spremljen na koraku „{step}”.',
  tryNow: 'Pokušaj sada',
  tryAgain: 'Pokušaj ponovno',
  tryWith: 'Pokušaj s modelom {model}',
  altPolicy: 'Postavke tvoje tvrtke ne dopuštaju drugi model.',
  altRegion: 'Drugi model nije dostupan u regiji obrade tvoje tvrtke.',
  altBudget: 'Drugi model premašio bi proračun tvoje tvrtke.',
  altNotReady: 'Drugi model još nije spreman za upotrebu.',
  buildingWith: 'Izrađuje se s modelom {model}',
  buildingVersion: 'v{n} se izrađuje s modelom {model}',
  versionKept: 'v{kept} ostaje kakva jest.',
  compareWith: 'Usporedi s v{n}',
  builderNotReady: 'Ova vrsta artefakta još se ne može izraditi u ovom radnom prostoru.',
  builderNotReadyState: 'Još nije spremno',
  builderNothingStarted: 'Nijedna izrada nije pokrenuta ni naplaćena.',
  builderNotAllowed: 'Postavke tvoje tvrtke ne dopuštaju izradu ove vrste artefakta.',
  builderNotInRegion: 'Ova vrsta artefakta ne može se izraditi u regiji obrade tvoje tvrtke.',
  builderOverBudget: 'Izrada ove vrste artefakta premašila bi proračun tvoje tvrtke.',
  buildLargerTakesMinutes: 'Izrada većih artefakata traje nekoliko minuta.',
  startBlankChat: 'Počni od praznog chata',
  elapsedMinSec: '{min} min {s} s',
};

const hu: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'Várakozás az indításra',
  phaseAcquiring: 'Munkaterület előkészítése',
  phaseAuthoring: 'Írás',
  phaseCompiling: 'Összeállítás',
  phaseRendering: 'Előnézetek készítése',
  phaseChecking: 'Működés ellenőrzése',
  phaseSaving: 'Mentés',
  phaseSettled: 'Kész',
  stepOf: '{n} / {total}',
  stillWorking: 'Még folyamatban: {step}',
  slowReason: 'A(z) {step} a szokásosnál tovább tart.',
  keepWaiting: 'Várakozás folytatása',
  stop: 'Leállítás',
  stopping: 'Leállítás folyamatban…',
  continue: 'Folytatás',
  fix: 'Javítás',
  fixInChat: 'Javítás a csevegésben',
  remoteWorking: '{agent} dolgozik',
  remoteWorkingUnnamed: 'A csatlakoztatott ügynök dolgozik',
  remoteLastSecond: 'Utolsó lépés: {seconds} másodperce',
  remoteLastMinute: 'Utolsó lépés: {minutes} perce',
  remoteNoStep: 'Még nem érkezett jelentés lépésről',
  builtWith: 'Ezzel készült: {model}',
  buildStartedChip: 'A készítés elindult',
  approvedBuilding: 'Jóváhagyva · Készül…',
  built: 'Elkészült',
  byAgent: 'Közvetítő: {agent}',
  agentNameReported: 'Az ügynök által megadott név',
  publishedBy: 'Közzétette: {agent}',
  elapsed: 'Eltelt idő',
  ready: '{kind}: kész',
  readyGeneric: 'Kész',
  confirming: 'Mentés megerősítése',
  steps: 'Lépések',
  outcomeUnknown: 'Lehet, hogy befejeződött, de ezt nem sikerült megerősíteni.',
  checkAgain: 'Újraellenőrzés',
  stopHere: 'Leállítás itt',
  budgetPaused:
    'Szüneteltetve a keret határán: {spent} / {limit}. Az utolsó működő verzió piszkozatként megmarad.',
  continueWith: 'Folytatás további {amount} kerettel',
  failed: 'Leállt, mielőtt elkészült volna.',
  stopped: 'Leállítva. A legutóbb mentett verzió változatlan.',
  stoppedNeutral: 'Leállítva.',
  blockedApproval: 'Jóváhagyásra vár',
  blockedBudget: 'Szüneteltetve a feladat költségkeretének határán',
  blockedProvider: 'A modellszolgáltatás jelenleg nem válaszol',
  blockedViewer: 'Be kell állítani valakit, aki ezt a megtekintők számára futtatja',
  blockedOther: 'Szüneteltetve',
  denied: 'Nincs hozzáférésed ehhez a feladathoz.',
  loading: 'Indítás…',
  shaping: 'Formálódik a kérésed alapján…',
  pendingValue: 'Még nincs kitöltve',
  unavailableValue: 'Nem érhető el',
  publishing: 'Közzététel…',
  fieldsCount: '{n} mező',
  busyNow: '{model} most foglalt.',
  busyHeld: '{model} foglalt.',
  busyStill: '{model} még mindig foglalt.',
  draftSaved: 'A piszkozatod mentve.',
  retryIn: 'Új próbálkozás {n} s múlva.',
  retryingNow: 'Új próbálkozás folyamatban.',
  checkingLastStep: 'Ellenőrzés: befejeződött-e az utolsó lépés…',
  continueFrom: 'Amint {model} szabad, a(z) „{step}” lépéstől folytatódik.',
  tryLater: 'Próbáld újra néhány perc múlva.',
  stoppedAt: 'Leállítva. A piszkozatod a(z) „{step}” lépésnél mentve.',
  tryNow: 'Próbáld most',
  tryAgain: 'Újrapróbálás',
  tryWith: 'Próbáld ezzel: {model}',
  altPolicy: 'A céged beállításai nem engedélyeznek másik modellt.',
  altRegion: 'Másik modell nem érhető el a céged adatfeldolgozási régiójában.',
  altBudget: 'Egy másik modell túllépné a céged költségkeretét.',
  altNotReady: 'Másik modell még nem áll készen a használatra.',
  buildingWith: 'Készítés ezzel: {model}',
  buildingVersion: 'v{n} készítése ezzel: {model}',
  versionKept: 'A v{kept} változatlan marad.',
  compareWith: 'Összehasonlítás: v{n}',
  builderNotReady: 'Ilyen típusú artefaktum ebben a munkaterületen még nem készíthető.',
  builderNotReadyState: 'Még nem áll készen',
  builderNothingStarted: 'Nem indult el készítés, és nem történt terhelés.',
  builderNotAllowed: 'A céged beállításai nem engedélyezik ilyen típusú artefaktum készítését.',
  builderNotInRegion: 'Ilyen típusú artefaktum nem készíthető a céged adatfeldolgozási régiójában.',
  builderOverBudget: 'Ilyen típusú artefaktum készítése túllépné a céged költségkeretét.',
  buildLargerTakesMinutes: 'A nagyobb artefaktumok elkészítése néhány percig tart.',
  startBlankChat: 'Kezdés üres csevegésből',
  elapsedMinSec: '{min} perc {s} mp',
};

const it: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'In attesa di iniziare',
  phaseAcquiring: 'Preparazione dell’area di lavoro',
  phaseAuthoring: 'Scrittura',
  phaseCompiling: 'Assemblaggio',
  phaseRendering: 'Creazione delle anteprime',
  phaseChecking: 'Verifica del funzionamento',
  phaseSaving: 'Salvataggio',
  phaseSettled: 'Completato',
  stepOf: '{n} di {total}',
  stillWorking: 'Ancora al lavoro su {step}',
  slowReason: '{step} sta richiedendo più tempo del solito.',
  keepWaiting: 'Continua ad attendere',
  stop: 'Interrompi',
  stopping: 'Interruzione in corso…',
  continue: 'Continua',
  fix: 'Correggi',
  fixInChat: 'Correggi in chat',
  remoteWorking: '{agent} sta lavorando',
  remoteWorkingUnnamed: 'L’agente collegato sta lavorando',
  remoteLastSecond: 'Ultimo passaggio {seconds} s fa',
  remoteLastMinute: 'Ultimo passaggio {minutes} min fa',
  remoteNoStep: 'Nessun passaggio ancora segnalato',
  builtWith: 'Creato con {model}',
  buildStartedChip: 'Creazione avviata',
  approvedBuilding: 'Approvato · In creazione…',
  built: 'Creato',
  byAgent: 'tramite {agent}',
  agentNameReported: 'Nome segnalato dall’agente',
  publishedBy: 'Pubblicato da {agent}',
  elapsed: 'Tempo trascorso',
  ready: '{kind}: pronto',
  readyGeneric: 'Pronto',
  confirming: 'Conferma del salvataggio',
  steps: 'Passaggi',
  outcomeUnknown: 'Il lavoro potrebbe essere terminato, ma non è stato possibile confermarlo.',
  checkAgain: 'Controlla di nuovo',
  stopHere: 'Interrompi qui',
  budgetPaused:
    'In pausa al limite del budget: {spent} di {limit}. L’ultima versione funzionante rimane una bozza.',
  continueWith: 'Continua con altri {amount}',
  failed: 'Il lavoro si è interrotto prima di essere completato.',
  stopped: 'Interrotto. L’ultima versione salvata è invariata.',
  stoppedNeutral: 'Interrotto.',
  blockedApproval: 'In attesa del tuo consenso',
  blockedBudget: 'In pausa al limite del budget di questa creazione',
  blockedProvider: 'Il servizio del modello non risponde al momento',
  blockedViewer: 'Occorre configurare qualcuno che lo esegua per chi visualizza',
  blockedOther: 'In pausa',
  denied: 'Non hai accesso a questa creazione.',
  loading: 'Avvio…',
  shaping: 'Prende forma dalla tua richiesta…',
  pendingValue: 'Non ancora compilato',
  unavailableValue: 'Non disponibile',
  publishing: 'Pubblicazione…',
  fieldsCount: '{n} campi',
  busyNow: '{model} è occupato in questo momento.',
  busyHeld: '{model} è occupato.',
  busyStill: '{model} è ancora occupato.',
  draftSaved: 'La tua bozza è salvata.',
  retryIn: 'Nuovo tentativo tra {n} s.',
  retryingNow: 'Nuovo tentativo in corso.',
  checkingLastStep: 'Verifica in corso: l’ultimo passaggio è terminato?…',
  continueFrom: 'Riprenderà dal passaggio «{step}» quando {model} sarà libero.',
  tryLater: 'Riprova tra qualche minuto.',
  stoppedAt: 'Interrotto. La tua bozza è salvata al passaggio «{step}».',
  tryNow: 'Prova ora',
  tryAgain: 'Riprova',
  tryWith: 'Prova con {model}',
  altPolicy: 'Le impostazioni della tua azienda non consentono un altro modello.',
  altRegion: 'Un altro modello non è disponibile nella regione di elaborazione della tua azienda.',
  altBudget: 'Un altro modello supererebbe il budget della tua azienda.',
  altNotReady: 'Un altro modello non è ancora pronto per l’uso.',
  buildingWith: 'Creazione con {model}',
  buildingVersion: 'Creazione di v{n} con {model}',
  versionKept: 'v{kept} resta com’è.',
  compareWith: 'Confronta con v{n}',
  builderNotReady:
    'Questo tipo di Artefatto non può ancora essere creato in questo spazio di lavoro.',
  builderNotReadyState: 'Non ancora pronto',
  builderNothingStarted: 'Nessuna creazione è stata avviata o addebitata.',
  builderNotAllowed:
    'Le impostazioni della tua azienda non consentono di creare questo tipo di Artefatto.',
  builderNotInRegion:
    'Questo tipo di Artefatto non può essere creato nella regione di elaborazione della tua azienda.',
  builderOverBudget: 'Creare questo tipo di Artefatto supererebbe il budget della tua azienda.',
  buildLargerTakesMinutes: 'Gli Artefatti più grandi richiedono qualche minuto.',
  startBlankChat: 'Inizia da una chat vuota',
  elapsedMinSec: '{min} min {s} s',
};

const lt: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'Laukiama pradžios',
  phaseAcquiring: 'Ruošiama darbo sritis',
  phaseAuthoring: 'Rašoma',
  phaseCompiling: 'Sudedama į visumą',
  phaseRendering: 'Kuriamos peržiūros',
  phaseChecking: 'Tikrinamas veikimas',
  phaseSaving: 'Išsaugoma',
  phaseSettled: 'Baigta',
  stepOf: '{n} iš {total}',
  stillWorking: 'Veiksmas {step} dar vykdomas',
  slowReason: 'Veiksmas {step} užtrunka ilgiau nei įprastai.',
  keepWaiting: 'Laukti toliau',
  stop: 'Sustabdyti',
  stopping: 'Stabdoma…',
  continue: 'Tęsti',
  fix: 'Pataisyti',
  fixInChat: 'Pataisyti pokalbyje',
  remoteWorking: '{agent} dirba',
  remoteWorkingUnnamed: 'Prijungtas agentas dirba',
  remoteLastSecond: 'Paskutinis veiksmas prieš {seconds} sek.',
  remoteLastMinute: 'Paskutinis veiksmas prieš {minutes} min.',
  remoteNoStep: 'Dar nepranešta apie jokį veiksmą',
  builtWith: 'Sukurta naudojant {model}',
  buildStartedChip: 'Kūrimas pradėtas',
  approvedBuilding: 'Patvirtinta · Kuriama…',
  built: 'Sukurta',
  byAgent: 'per {agent}',
  agentNameReported: 'Agento nurodytas vardas',
  publishedBy: 'Paskelbė: {agent}',
  elapsed: 'Praėjęs laikas',
  ready: '{kind}: paruošta',
  readyGeneric: 'Paruošta',
  confirming: 'Patvirtinamas išsaugojimas',
  steps: 'Veiksmai',
  outcomeUnknown: 'Darbas galėjo būti baigtas, bet to nepavyko patvirtinti.',
  checkAgain: 'Tikrinti dar kartą',
  stopHere: 'Sustabdyti čia',
  budgetPaused:
    'Pristabdyta pasiekus biudžeto ribą: {spent} iš {limit}. Paskutinė veikianti versija lieka juodraščiu.',
  continueWith: 'Tęsti pridėjus {amount}',
  failed: 'Darbas sustojo dar nebaigtas.',
  stopped: 'Sustabdyta. Paskutinė išsaugota versija nepakeista.',
  stoppedNeutral: 'Sustabdyta.',
  blockedApproval: 'Laukiama jūsų patvirtinimo',
  blockedBudget: 'Pristabdyta pasiekus šio darbo biudžeto ribą',
  blockedProvider: 'Modelio paslauga šiuo metu neatsako',
  blockedViewer: 'Reikia paskirti asmenį, kuris tai vykdys peržiūrintiesiems',
  blockedOther: 'Pristabdyta',
  denied: 'Neturite prieigos prie šio darbo.',
  loading: 'Paleidžiama…',
  shaping: 'Formuojama pagal jūsų užklausą…',
  pendingValue: 'Dar neužpildyta',
  unavailableValue: 'Nepasiekiama',
  publishing: 'Skelbiama…',
  fieldsCount: 'Laukai: {n}',
  busyNow: '{model} šiuo metu užimtas.',
  busyHeld: '{model} užimtas.',
  busyStill: '{model} vis dar užimtas.',
  draftSaved: 'Jūsų juodraštis išsaugotas.',
  retryIn: 'Kitas bandymas po {n} s.',
  retryingNow: 'Bandoma iš naujo.',
  checkingLastStep: 'Tikrinama, ar paskutinis žingsnis baigtas…',
  continueFrom: 'Kai {model} atsilaisvins, bus tęsiama nuo žingsnio „{step}“.',
  tryLater: 'Bandykite dar kartą po kelių minučių.',
  stoppedAt: 'Sustabdyta. Jūsų juodraštis išsaugotas ties žingsniu „{step}“.',
  tryNow: 'Bandyti dabar',
  tryAgain: 'Bandyti dar kartą',
  tryWith: 'Bandyti su {model}',
  altPolicy: 'Jūsų įmonės nustatymai neleidžia naudoti kito modelio.',
  altRegion: 'Kitas modelis nepasiekiamas jūsų įmonės duomenų tvarkymo regione.',
  altBudget: 'Kitas modelis viršytų jūsų įmonės biudžetą.',
  altNotReady: 'Kitas modelis dar neparengtas naudoti.',
  buildingWith: 'Kuriama su {model}',
  buildingVersion: 'Kuriama v{n} su {model}',
  versionKept: 'v{kept} lieka nepakeista.',
  compareWith: 'Palyginti su v{n}',
  builderNotReady: 'Šios rūšies artefakto šioje darbo srityje dar negalima sukurti.',
  builderNotReadyState: 'Dar neparuošta',
  builderNothingStarted: 'Joks kūrimas nebuvo paleistas ir neapmokestintas.',
  builderNotAllowed: 'Jūsų įmonės nustatymai neleidžia kurti šios rūšies artefakto.',
  builderNotInRegion:
    'Šios rūšies artefakto negalima sukurti jūsų įmonės duomenų tvarkymo regione.',
  builderOverBudget: 'Šios rūšies artefakto kūrimas viršytų jūsų įmonės biudžetą.',
  buildLargerTakesMinutes: 'Didesnių artefaktų kūrimas užtrunka kelias minutes.',
  startBlankChat: 'Pradėti nuo tuščio pokalbio',
  elapsedMinSec: '{min} min. {s} s',
};

const lv: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'Gaida sākšanu',
  phaseAcquiring: 'Sagatavo darbvietu',
  phaseAuthoring: 'Raksta',
  phaseCompiling: 'Saliek kopā',
  phaseRendering: 'Veido priekšskatījumus',
  phaseChecking: 'Pārbauda darbību',
  phaseSaving: 'Saglabā',
  phaseSettled: 'Pabeigts',
  stepOf: '{n} no {total}',
  stillWorking: 'Joprojām notiek solis: {step}',
  slowReason: 'Solis {step} aizņem vairāk laika nekā parasti.',
  keepWaiting: 'Turpināt gaidīt',
  stop: 'Apturēt',
  stopping: 'Aptur…',
  continue: 'Turpināt',
  fix: 'Labot',
  fixInChat: 'Labot tērzēšanā',
  remoteWorking: '{agent} strādā',
  remoteWorkingUnnamed: 'Pievienotais aģents strādā',
  remoteLastSecond: 'Pēdējais solis pirms {seconds} s',
  remoteLastMinute: 'Pēdējais solis pirms {minutes} min',
  remoteNoStep: 'Vēl nav ziņots par nevienu soli',
  builtWith: 'Izveidots ar {model}',
  buildStartedChip: 'Veidošana sākta',
  approvedBuilding: 'Apstiprināts · Tiek izveidots…',
  built: 'Izveidots',
  byAgent: 'izmantojot {agent}',
  agentNameReported: 'Aģenta norādītais vārds',
  publishedBy: 'Publicēja: {agent}',
  elapsed: 'Pagājušais laiks',
  ready: '{kind}: gatavs',
  readyGeneric: 'Gatavs',
  confirming: 'Apstiprina saglabāšanu',
  steps: 'Soļi',
  outcomeUnknown: 'Iespējams, darbs ir pabeigts, taču to neizdevās apstiprināt.',
  checkAgain: 'Pārbaudīt vēlreiz',
  stopHere: 'Apturēt šeit',
  budgetPaused:
    'Pauzēts pie budžeta robežas: {spent} no {limit}. Pēdējā darbspējīgā versija paliek kā melnraksts.',
  continueWith: 'Turpināt, pievienojot {amount}',
  failed: 'Darbs apstājās pirms pabeigšanas.',
  stopped: 'Apturēts. Pēdējā saglabātā versija nav mainīta.',
  stoppedNeutral: 'Apturēts.',
  blockedApproval: 'Gaida tavu apstiprinājumu',
  blockedBudget: 'Pauzēts pie šī darba budžeta robežas',
  blockedProvider: 'Modeļa pakalpojums pašlaik neatbild',
  blockedViewer: 'Jāiestata persona, kas to izpildīs skatītājiem',
  blockedOther: 'Pauzēts',
  denied: 'Tev nav piekļuves šim darbam.',
  loading: 'Sāk…',
  shaping: 'Veido pēc tava pieprasījuma…',
  pendingValue: 'Vēl nav aizpildīts',
  unavailableValue: 'Nav pieejams',
  publishing: 'Publicē…',
  fieldsCount: 'Lauki: {n}',
  busyNow: '{model} pašlaik ir aizņemts.',
  busyHeld: '{model} ir aizņemts.',
  busyStill: '{model} joprojām ir aizņemts.',
  draftSaved: 'Tavs melnraksts ir saglabāts.',
  retryIn: 'Nākamais mēģinājums pēc {n} s.',
  retryingNow: 'Notiek atkārtots mēģinājums.',
  checkingLastStep: 'Tiek pārbaudīts, vai pēdējais solis ir pabeigts…',
  continueFrom: 'Kad {model} būs brīvs, darbs turpināsies no soļa “{step}”.',
  tryLater: 'Mēģini vēlreiz pēc dažām minūtēm.',
  stoppedAt: 'Apturēts. Tavs melnraksts ir saglabāts pie soļa “{step}”.',
  tryNow: 'Mēģināt tagad',
  tryAgain: 'Mēģināt vēlreiz',
  tryWith: 'Mēģināt ar {model}',
  altPolicy: 'Tava uzņēmuma iestatījumi neatļauj citu modeli.',
  altRegion: 'Cits modelis nav pieejams tava uzņēmuma apstrādes reģionā.',
  altBudget: 'Cits modelis pārsniegtu tava uzņēmuma budžetu.',
  altNotReady: 'Cits modelis vēl nav gatavs lietošanai.',
  buildingWith: 'Tiek veidots ar {model}',
  buildingVersion: 'Tiek veidota v{n} ar {model}',
  versionKept: 'v{kept} paliek nemainīga.',
  compareWith: 'Salīdzināt ar v{n}',
  builderNotReady: 'Šāda veida artefaktu šajā darbvietā vēl nevar izveidot.',
  builderNotReadyState: 'Vēl nav gatavs',
  builderNothingStarted: 'Neviena veidošana netika sākta vai iekasēta.',
  builderNotAllowed: 'Tava uzņēmuma iestatījumi neatļauj veidot šāda veida artefaktus.',
  builderNotInRegion: 'Šāda veida artefaktu nevar izveidot tava uzņēmuma apstrādes reģionā.',
  builderOverBudget: 'Šāda veida artefakta veidošana pārsniegtu tava uzņēmuma budžetu.',
  buildLargerTakesMinutes: 'Lielāku artefaktu veidošana aizņem dažas minūtes.',
  startBlankChat: 'Sākt no tukšas tērzēšanas',
  elapsedMinSec: '{min} min {s} s',
};

const mt: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'Qed jistenna biex jibda',
  phaseAcquiring: 'Qed jitħejja spazju tax-xogħol',
  phaseAuthoring: 'Kitba',
  phaseCompiling: 'Qed jinġabar kollox flimkien',
  phaseRendering: 'Qed jinħolqu previżjonijiet',
  phaseChecking: 'Qed jiġi vverifikat li jaħdem',
  phaseSaving: 'Qed jiġi ssejvjat',
  phaseSettled: 'Lest',
  stepOf: '{n} minn {total}',
  stillWorking: 'Għadu għaddej ix-xogħol fuq {step}',
  slowReason: '{step} qed jieħu aktar żmien mis-soltu.',
  keepWaiting: 'Kompli stenna',
  stop: 'Waqqaf',
  stopping: 'Qed jieqaf…',
  continue: 'Kompli',
  fix: 'Irranġa',
  fixInChat: 'Irranġa fiċ-chat',
  remoteWorking: '{agent} qed jaħdem',
  remoteWorkingUnnamed: 'L-aġent konness qed jaħdem',
  remoteLastSecond: 'L-aħħar pass {seconds} s ilu',
  remoteLastMinute: 'L-aħħar pass {minutes} min ilu',
  remoteNoStep: 'Għadu ma ġie rrappurtat l-ebda pass',
  builtWith: 'Maħluq b’{model}',
  buildStartedChip: 'Il-bini beda',
  approvedBuilding: 'Approvat · Qed jinħoloq…',
  built: 'Maħluq',
  byAgent: 'permezz ta’ {agent}',
  agentNameReported: 'L-isem irrappurtat mill-aġent',
  publishedBy: 'Ippubblikat minn {agent}',
  elapsed: 'Ħin li għadda',
  ready: '{kind}: lest',
  readyGeneric: 'Lest',
  confirming: 'Qed jiġi kkonfermat li ġie ssejvjat',
  steps: 'Passi',
  outcomeUnknown: 'Jista’ jkun li spiċċa, iżda dan ma setax jiġi kkonfermat.',
  checkAgain: 'Erġa’ ċċekkja',
  stopHere: 'Waqqaf hawn',
  budgetPaused:
    'Waqfa fil-limitu tal-baġit: {spent} minn {limit}. L-aħħar verżjoni li taħdem tibqa’ abbozz.',
  continueWith: 'Kompli b’{amount} oħra',
  failed: 'Waqaf qabel ma kien lest.',
  stopped: 'Imwaqqaf. L-aħħar verżjoni ssejvjata ma nbidlitx.',
  stoppedNeutral: 'Imwaqqaf.',
  blockedApproval: 'Qed jistenna l-approvazzjoni tiegħek',
  blockedBudget: 'Waqfa fil-limitu tal-baġit għal dan ix-xogħol',
  blockedProvider: 'Is-servizz tal-mudell mhux qed iwieġeb bħalissa',
  blockedViewer: 'Jeħtieġ jiġi ssettjat xi ħadd biex iħaddem dan għal min jarah',
  blockedOther: 'Wieqaf temporanjament',
  denied: 'M’għandekx aċċess għal dan ix-xogħol.',
  loading: 'Qed jibda…',
  shaping: 'Qed jieħu forma mit-talba tiegħek…',
  pendingValue: 'Għadu mhux mimli',
  unavailableValue: 'Mhux disponibbli',
  publishing: 'Qed jiġi ppubblikat…',
  fieldsCount: '{n} oqsma',
  busyNow: '{model} huwa okkupat bħalissa.',
  busyHeld: '{model} huwa okkupat.',
  busyStill: '{model} għadu okkupat.',
  draftSaved: 'L-abbozz tiegħek huwa ssejvjat.',
  retryIn: 'Jerġa’ jipprova fi {n} s.',
  retryingNow: 'Qed jerġa’ jipprova issa.',
  checkingLastStep: 'Qed jiċċekkja jekk l-aħħar pass intemmx…',
  continueFrom: 'Se jkompli mill-pass “{step}” meta {model} ikun liberu.',
  tryLater: 'Erġa’ pprova fi ftit minuti.',
  stoppedAt: 'Imwaqqaf. L-abbozz tiegħek huwa ssejvjat fil-pass “{step}”.',
  tryNow: 'Ipprova issa',
  tryAgain: 'Erġa’ pprova',
  tryWith: 'Ipprova b’{model}',
  altPolicy: 'L-issettjar tal-kumpanija tiegħek ma jippermettix mudell ieħor.',
  altRegion: 'Mudell ieħor mhuwiex disponibbli fir-reġjun tal-ipproċessar tal-kumpanija tiegħek.',
  altBudget: 'Mudell ieħor jaqbeż il-baġit tal-kumpanija tiegħek.',
  altNotReady: 'Mudell ieħor għadu mhux lest biex jintuża.',
  buildingWith: 'Qed jinbena b’{model}',
  buildingVersion: 'Qed tinbena v{n} b’{model}',
  versionKept: 'v{kept} tibqa’ kif inhi.',
  compareWith: 'Qabbel ma’ v{n}',
  builderNotReady: 'Dan it-tip ta’ artifatt għadu ma jistax jinbena f’dan l-ispazju tax-xogħol.',
  builderNotReadyState: 'Għadu mhux lest',
  builderNothingStarted: 'Ebda bini ma beda u ma ġie ddebitat.',
  builderNotAllowed:
    'L-issettjar tal-kumpanija tiegħek ma jippermettix il-bini ta’ dan it-tip ta’ artifatt.',
  builderNotInRegion:
    'Dan it-tip ta’ artifatt ma jistax jinbena fir-reġjun tal-ipproċessar tal-kumpanija tiegħek.',
  builderOverBudget: 'Il-bini ta’ dan it-tip ta’ artifatt jaqbeż il-baġit tal-kumpanija tiegħek.',
  buildLargerTakesMinutes: 'Artifatti akbar jieħdu ftit minuti biex jinbnew.',
  startBlankChat: 'Ibda minn chat vojt',
  elapsedMinSec: '{min} min {s} s',
};

const nl: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'Wachten om te beginnen',
  phaseAcquiring: 'Een werkruimte voorbereiden',
  phaseAuthoring: 'Schrijven',
  phaseCompiling: 'Samenstellen',
  phaseRendering: 'Voorbeelden maken',
  phaseChecking: 'Controleren of het werkt',
  phaseSaving: 'Opslaan',
  phaseSettled: 'Klaar',
  stepOf: '{n} van {total}',
  stillWorking: 'Nog bezig met {step}',
  slowReason: '{step} duurt langer dan normaal.',
  keepWaiting: 'Blijven wachten',
  stop: 'Stoppen',
  stopping: 'Bezig met stoppen…',
  continue: 'Doorgaan',
  fix: 'Herstellen',
  fixInChat: 'Herstellen in de chat',
  remoteWorking: '{agent} is bezig',
  remoteWorkingUnnamed: 'De verbonden agent is bezig',
  remoteLastSecond: 'Laatste stap {seconds} s geleden',
  remoteLastMinute: 'Laatste stap {minutes} min geleden',
  remoteNoStep: 'Nog geen stap gemeld',
  builtWith: 'Gemaakt met {model}',
  buildStartedChip: 'Maakproces gestart',
  approvedBuilding: 'Goedgekeurd · Wordt gemaakt…',
  built: 'Gemaakt',
  byAgent: 'door {agent}',
  agentNameReported: 'Naam die de agent heeft opgegeven',
  publishedBy: 'Gepubliceerd door {agent}',
  elapsed: 'Verstreken tijd',
  ready: '{kind} is klaar',
  readyGeneric: 'Klaar',
  confirming: 'Bevestigen dat het is opgeslagen',
  steps: 'Stappen',
  outcomeUnknown: 'Het werk is mogelijk klaar, maar dat kon niet worden bevestigd.',
  checkAgain: 'Opnieuw controleren',
  stopHere: 'Hier stoppen',
  budgetPaused:
    'Gepauzeerd bij de budgetlimiet: {spent} van {limit}. De laatste werkende versie blijft een concept.',
  continueWith: 'Doorgaan met {amount} extra',
  failed: 'Het werk stopte voordat het klaar was.',
  stopped: 'Gestopt. De laatst opgeslagen versie is ongewijzigd.',
  stoppedNeutral: 'Gestopt.',
  blockedApproval: 'Wachten op je toestemming',
  blockedBudget: 'Gepauzeerd bij de budgetlimiet voor deze creatie',
  blockedProvider: 'De modeldienst reageert momenteel niet',
  blockedViewer: 'Er moet iemand worden ingesteld om dit voor kijkers uit te voeren',
  blockedOther: 'Gepauzeerd',
  denied: 'Je hebt geen toegang tot deze creatie.',
  loading: 'Starten…',
  shaping: 'Vormgeven op basis van je verzoek…',
  pendingValue: 'Nog niet ingevuld',
  unavailableValue: 'Niet beschikbaar',
  publishing: 'Publiceren…',
  fieldsCount: '{n} velden',
  busyNow: '{model} is op dit moment bezet.',
  busyHeld: '{model} is bezet.',
  busyStill: '{model} is nog steeds bezet.',
  draftSaved: 'Je concept is opgeslagen.',
  retryIn: 'Nieuwe poging over {n} s.',
  retryingNow: 'Nieuwe poging loopt.',
  checkingLastStep: 'Controleren of de laatste stap klaar is…',
  continueFrom: 'Het gaat verder vanaf de stap ‘{step}’ zodra {model} vrij is.',
  tryLater: 'Probeer het over een paar minuten opnieuw.',
  stoppedAt: 'Gestopt. Je concept is opgeslagen bij de stap ‘{step}’.',
  tryNow: 'Nu proberen',
  tryAgain: 'Opnieuw proberen',
  tryWith: 'Proberen met {model}',
  altPolicy: 'De instellingen van je bedrijf staan geen ander model toe.',
  altRegion: 'Een ander model is niet beschikbaar in de verwerkingsregio van je bedrijf.',
  altBudget: 'Een ander model zou het budget van je bedrijf overschrijden.',
  altNotReady: 'Een ander model is nog niet klaar voor gebruik.',
  buildingWith: 'Wordt gemaakt met {model}',
  buildingVersion: 'v{n} wordt gemaakt met {model}',
  versionKept: 'v{kept} blijft zoals hij is.',
  compareWith: 'Vergelijken met v{n}',
  builderNotReady: 'Dit soort artefact kan in deze werkruimte nog niet worden gemaakt.',
  builderNotReadyState: 'Nog niet klaar',
  builderNothingStarted: 'Er is geen maakproces gestart of in rekening gebracht.',
  builderNotAllowed:
    'De instellingen van je bedrijf staan niet toe dat dit soort artefact wordt gemaakt.',
  builderNotInRegion:
    'Dit soort artefact kan niet worden gemaakt in de verwerkingsregio van je bedrijf.',
  builderOverBudget: 'Dit soort artefact maken zou het budget van je bedrijf overschrijden.',
  buildLargerTakesMinutes: 'Grotere artefacten maken duurt een paar minuten.',
  startBlankChat: 'Begin met een lege chat',
  elapsedMinSec: '{min} min {s} s',
};

const pl: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'Oczekiwanie na rozpoczęcie',
  phaseAcquiring: 'Przygotowywanie obszaru roboczego',
  phaseAuthoring: 'Pisanie',
  phaseCompiling: 'Składanie całości',
  phaseRendering: 'Tworzenie podglądów',
  phaseChecking: 'Sprawdzanie działania',
  phaseSaving: 'Zapisywanie',
  phaseSettled: 'Gotowe',
  stepOf: '{n} z {total}',
  stillWorking: 'Nadal trwa praca nad etapem: {step}',
  slowReason: 'Etap {step} trwa dłużej niż zwykle.',
  keepWaiting: 'Czekaj dalej',
  stop: 'Zatrzymaj',
  stopping: 'Zatrzymywanie…',
  continue: 'Kontynuuj',
  fix: 'Popraw',
  fixInChat: 'Popraw na czacie',
  remoteWorking: '{agent} pracuje',
  remoteWorkingUnnamed: 'Połączony agent pracuje',
  remoteLastSecond: 'Ostatni krok {seconds} s temu',
  remoteLastMinute: 'Ostatni krok {minutes} min temu',
  remoteNoStep: 'Nie zgłoszono jeszcze żadnego kroku',
  builtWith: 'Utworzono z użyciem {model}',
  buildStartedChip: 'Tworzenie rozpoczęte',
  approvedBuilding: 'Zatwierdzone · Tworzenie…',
  built: 'Utworzono',
  byAgent: 'za pośrednictwem {agent}',
  agentNameReported: 'Nazwa podana przez agenta',
  publishedBy: 'Opublikowano przez {agent}',
  elapsed: 'Dotychczasowy czas',
  ready: '{kind}: gotowe',
  readyGeneric: 'Gotowe',
  confirming: 'Potwierdzanie zapisu',
  steps: 'Kroki',
  outcomeUnknown: 'Praca mogła się zakończyć, ale nie udało się tego potwierdzić.',
  checkAgain: 'Sprawdź ponownie',
  stopHere: 'Zatrzymaj tutaj',
  budgetPaused:
    'Wstrzymano po osiągnięciu limitu budżetu: {spent} z {limit}. Ostatnia działająca wersja pozostaje wersją roboczą.',
  continueWith: 'Kontynuuj z dodatkowym budżetem {amount}',
  failed: 'Praca zatrzymała się przed ukończeniem.',
  stopped: 'Zatrzymano. Ostatnia zapisana wersja pozostaje bez zmian.',
  stoppedNeutral: 'Zatrzymano.',
  blockedApproval: 'Oczekiwanie na Twoją zgodę',
  blockedBudget: 'Wstrzymano na limicie budżetu tego zadania',
  blockedProvider: 'Usługa modelu teraz nie odpowiada',
  blockedViewer: 'Trzeba skonfigurować osobę, która będzie to uruchamiać dla oglądających',
  blockedOther: 'Wstrzymano',
  denied: 'Nie masz dostępu do tego zadania.',
  loading: 'Uruchamianie…',
  shaping: 'Nadawanie kształtu na podstawie Twojej prośby…',
  pendingValue: 'Jeszcze nie wypełniono',
  unavailableValue: 'Niedostępne',
  publishing: 'Publikowanie…',
  fieldsCount: 'Pola: {n}',
  busyNow: '{model} jest teraz zajęty.',
  busyHeld: '{model} jest zajęty.',
  busyStill: '{model} jest nadal zajęty.',
  draftSaved: 'Twój szkic jest zapisany.',
  retryIn: 'Kolejna próba za {n} s.',
  retryingNow: 'Trwa kolejna próba.',
  checkingLastStep: 'Sprawdzanie, czy ostatni krok się zakończył…',
  continueFrom: 'Gdy {model} będzie wolny, praca będzie kontynuowana od kroku „{step}”.',
  tryLater: 'Spróbuj ponownie za kilka minut.',
  stoppedAt: 'Zatrzymano. Twój szkic jest zapisany na kroku „{step}”.',
  tryNow: 'Spróbuj teraz',
  tryAgain: 'Spróbuj ponownie',
  tryWith: 'Spróbuj z {model}',
  altPolicy: 'Ustawienia Twojej firmy nie zezwalają na inny model.',
  altRegion: 'Inny model nie jest dostępny w regionie przetwarzania Twojej firmy.',
  altBudget: 'Inny model przekroczyłby budżet Twojej firmy.',
  altNotReady: 'Inny model nie jest jeszcze gotowy do użycia.',
  buildingWith: 'Tworzenie z {model}',
  buildingVersion: 'Tworzenie v{n} z {model}',
  versionKept: 'v{kept} pozostaje bez zmian.',
  compareWith: 'Porównaj z v{n}',
  builderNotReady: 'Tego rodzaju artefaktu nie można jeszcze utworzyć w tej przestrzeni roboczej.',
  builderNotReadyState: 'Jeszcze niegotowe',
  builderNothingStarted: 'Nie uruchomiono ani nie naliczono żadnego tworzenia.',
  builderNotAllowed: 'Ustawienia Twojej firmy nie zezwalają na tworzenie tego rodzaju artefaktu.',
  builderNotInRegion:
    'Tego rodzaju artefaktu nie można utworzyć w regionie przetwarzania Twojej firmy.',
  builderOverBudget: 'Utworzenie tego rodzaju artefaktu przekroczyłoby budżet Twojej firmy.',
  buildLargerTakesMinutes: 'Tworzenie większych artefaktów trwa kilka minut.',
  startBlankChat: 'Zacznij od pustego czatu',
  elapsedMinSec: '{min} min {s} s',
};

const pt: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'À espera de começar',
  phaseAcquiring: 'A preparar um espaço de trabalho',
  phaseAuthoring: 'A escrever',
  phaseCompiling: 'A montar',
  phaseRendering: 'A criar pré-visualizações',
  phaseChecking: 'A verificar o funcionamento',
  phaseSaving: 'A guardar',
  phaseSettled: 'Concluído',
  stepOf: '{n} de {total}',
  stillWorking: 'Ainda a trabalhar em {step}',
  slowReason: '{step} está a demorar mais do que o habitual.',
  keepWaiting: 'Continuar à espera',
  stop: 'Parar',
  stopping: 'A parar…',
  continue: 'Continuar',
  fix: 'Corrigir',
  fixInChat: 'Corrigir no chat',
  remoteWorking: '{agent} está a trabalhar',
  remoteWorkingUnnamed: 'O agente ligado está a trabalhar',
  remoteLastSecond: 'Último passo há {seconds} s',
  remoteLastMinute: 'Último passo há {minutes} min',
  remoteNoStep: 'Ainda não foi comunicado nenhum passo',
  builtWith: 'Criado com {model}',
  buildStartedChip: 'Criação iniciada',
  approvedBuilding: 'Aprovado · A criar…',
  built: 'Criado',
  byAgent: 'através de {agent}',
  agentNameReported: 'Nome comunicado pelo agente',
  publishedBy: 'Publicado por {agent}',
  elapsed: 'Tempo decorrido',
  ready: '{kind}: pronto',
  readyGeneric: 'Pronto',
  confirming: 'A confirmar que foi guardado',
  steps: 'Passos',
  outcomeUnknown: 'Pode ter terminado, mas não foi possível confirmar.',
  checkAgain: 'Verificar novamente',
  stopHere: 'Parar aqui',
  budgetPaused:
    'Em pausa no limite do orçamento: {spent} de {limit}. A última versão funcional mantém-se como rascunho.',
  continueWith: 'Continuar com mais {amount}',
  failed: 'Parou antes de ficar pronto.',
  stopped: 'Parado. A última versão guardada mantém-se inalterada.',
  stoppedNeutral: 'Parado.',
  blockedApproval: 'À espera da sua aprovação',
  blockedBudget: 'Em pausa no limite do orçamento desta criação',
  blockedProvider: 'O serviço do modelo não está a responder de momento',
  blockedViewer: 'É necessário configurar alguém para executar isto para quem visualiza',
  blockedOther: 'Em pausa',
  denied: 'Não tem acesso a esta criação.',
  loading: 'A iniciar…',
  shaping: 'A dar forma ao seu pedido…',
  pendingValue: 'Ainda não preenchido',
  unavailableValue: 'Indisponível',
  publishing: 'A publicar…',
  fieldsCount: '{n} campos',
  busyNow: 'O {model} está ocupado neste momento.',
  busyHeld: 'O {model} está ocupado.',
  busyStill: 'O {model} continua ocupado.',
  draftSaved: 'O seu rascunho está guardado.',
  retryIn: 'Nova tentativa dentro de {n} s.',
  retryingNow: 'Nova tentativa em curso.',
  checkingLastStep: 'A verificar se o último passo terminou…',
  continueFrom: 'Vai continuar a partir do passo «{step}» quando o {model} estiver livre.',
  tryLater: 'Tente novamente dentro de alguns minutos.',
  stoppedAt: 'Parado. O seu rascunho está guardado no passo «{step}».',
  tryNow: 'Tentar agora',
  tryAgain: 'Tentar novamente',
  tryWith: 'Tentar com {model}',
  altPolicy: 'As definições da sua empresa não permitem outro modelo.',
  altRegion: 'Outro modelo não está disponível na região de processamento da sua empresa.',
  altBudget: 'Outro modelo ultrapassaria o orçamento da sua empresa.',
  altNotReady: 'Outro modelo ainda não está pronto a ser utilizado.',
  buildingWith: 'A criar com {model}',
  buildingVersion: 'A criar v{n} com {model}',
  versionKept: 'A v{kept} fica como está.',
  compareWith: 'Comparar com v{n}',
  builderNotReady: 'Este tipo de Artefacto ainda não pode ser criado neste espaço de trabalho.',
  builderNotReadyState: 'Ainda não está pronto',
  builderNothingStarted: 'Nenhuma criação foi iniciada nem cobrada.',
  builderNotAllowed: 'As definições da sua empresa não permitem criar este tipo de Artefacto.',
  builderNotInRegion:
    'Este tipo de Artefacto não pode ser criado na região de processamento da sua empresa.',
  builderOverBudget: 'Criar este tipo de Artefacto ultrapassaria o orçamento da sua empresa.',
  buildLargerTakesMinutes: 'Artefactos maiores demoram alguns minutos a criar.',
  startBlankChat: 'Começar a partir de um chat em branco',
  elapsedMinSec: '{min} min {s} s',
};

const ro: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'În așteptarea pornirii',
  phaseAcquiring: 'Se pregătește spațiul de lucru',
  phaseAuthoring: 'Se scrie',
  phaseCompiling: 'Se asamblează',
  phaseRendering: 'Se creează previzualizări',
  phaseChecking: 'Se verifică funcționarea',
  phaseSaving: 'Se salvează',
  phaseSettled: 'Terminat',
  stepOf: '{n} din {total}',
  stillWorking: 'Se lucrează în continuare la {step}',
  slowReason: '{step} durează mai mult decât de obicei.',
  keepWaiting: 'Așteaptă în continuare',
  stop: 'Oprește',
  stopping: 'Se oprește…',
  continue: 'Continuă',
  fix: 'Corectează',
  fixInChat: 'Corectează în chat',
  remoteWorking: '{agent} lucrează',
  remoteWorkingUnnamed: 'Agentul conectat lucrează',
  remoteLastSecond: 'Ultimul pas acum {seconds} s',
  remoteLastMinute: 'Ultimul pas acum {minutes} min',
  remoteNoStep: 'Nu a fost raportat încă niciun pas',
  builtWith: 'Creat cu {model}',
  buildStartedChip: 'Crearea a început',
  approvedBuilding: 'Aprobat · Se creează…',
  built: 'Creat',
  byAgent: 'prin {agent}',
  agentNameReported: 'Numele raportat de agent',
  publishedBy: 'Publicat de {agent}',
  elapsed: 'Timp scurs',
  ready: '{kind}: gata',
  readyGeneric: 'Gata',
  confirming: 'Se confirmă salvarea',
  steps: 'Pași',
  outcomeUnknown: 'Este posibil să se fi terminat, dar acest lucru nu a putut fi confirmat.',
  checkAgain: 'Verifică din nou',
  stopHere: 'Oprește aici',
  budgetPaused:
    'În pauză la limita bugetului: {spent} din {limit}. Ultima versiune funcțională rămâne o ciornă.',
  continueWith: 'Continuă cu încă {amount}',
  failed: 'S-a oprit înainte de finalizare.',
  stopped: 'Oprit. Ultima versiune salvată este neschimbată.',
  stoppedNeutral: 'Oprit.',
  blockedApproval: 'Se așteaptă acordul tău',
  blockedBudget: 'În pauză la limita bugetului acestei lucrări',
  blockedProvider: 'Serviciul modelului nu răspunde momentan',
  blockedViewer: 'Trebuie desemnată o persoană care să ruleze aceasta pentru cei care vizualizează',
  blockedOther: 'În pauză',
  denied: 'Nu ai acces la această lucrare.',
  loading: 'Se pornește…',
  shaping: 'Prinde formă pe baza cererii tale…',
  pendingValue: 'Încă necompletat',
  unavailableValue: 'Indisponibil',
  publishing: 'Se publică…',
  fieldsCount: '{n} câmpuri',
  busyNow: '{model} este ocupat acum.',
  busyHeld: '{model} este ocupat.',
  busyStill: '{model} este încă ocupat.',
  draftSaved: 'Ciorna ta este salvată.',
  retryIn: 'O nouă încercare în {n} s.',
  retryingNow: 'Se încearcă din nou.',
  checkingLastStep: 'Se verifică dacă ultimul pas s-a încheiat…',
  continueFrom: 'Va continua de la pasul „{step}” când {model} va fi liber.',
  tryLater: 'Încearcă din nou peste câteva minute.',
  stoppedAt: 'Oprit. Ciorna ta este salvată la pasul „{step}”.',
  tryNow: 'Încearcă acum',
  tryAgain: 'Încearcă din nou',
  tryWith: 'Încearcă cu {model}',
  altPolicy: 'Setările companiei tale nu permit alt model.',
  altRegion: 'Alt model nu este disponibil în regiunea de procesare a companiei tale.',
  altBudget: 'Alt model ar depăși bugetul companiei tale.',
  altNotReady: 'Alt model nu este încă pregătit de utilizare.',
  buildingWith: 'Se creează cu {model}',
  buildingVersion: 'Se creează v{n} cu {model}',
  versionKept: 'v{kept} rămâne neschimbată.',
  compareWith: 'Compară cu v{n}',
  builderNotReady: 'Acest tip de artefact nu poate fi creat încă în acest spațiu de lucru.',
  builderNotReadyState: 'Încă nu este pregătit',
  builderNothingStarted: 'Nicio creare nu a fost pornită sau taxată.',
  builderNotAllowed: 'Setările companiei tale nu permit crearea acestui tip de artefact.',
  builderNotInRegion:
    'Acest tip de artefact nu poate fi creat în regiunea de procesare a companiei tale.',
  builderOverBudget: 'Crearea acestui tip de artefact ar depăși bugetul companiei tale.',
  buildLargerTakesMinutes: 'Artefactele mai mari durează câteva minute.',
  startBlankChat: 'Începe de la un chat gol',
  elapsedMinSec: '{min} min. {s} s',
};

const sk: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'Čaká sa na spustenie',
  phaseAcquiring: 'Príprava pracovného priestoru',
  phaseAuthoring: 'Písanie',
  phaseCompiling: 'Zostavovanie',
  phaseRendering: 'Vytváranie náhľadov',
  phaseChecking: 'Overovanie funkčnosti',
  phaseSaving: 'Ukladanie',
  phaseSettled: 'Hotovo',
  stepOf: '{n} z {total}',
  stillWorking: 'Stále prebieha krok: {step}',
  slowReason: 'Krok {step} trvá dlhšie než zvyčajne.',
  keepWaiting: 'Čakať ďalej',
  stop: 'Zastaviť',
  stopping: 'Zastavovanie…',
  continue: 'Pokračovať',
  fix: 'Opraviť',
  fixInChat: 'Opraviť v čete',
  remoteWorking: '{agent} pracuje',
  remoteWorkingUnnamed: 'Pripojený agent pracuje',
  remoteLastSecond: 'Posledný krok pred {seconds} s',
  remoteLastMinute: 'Posledný krok pred {minutes} min',
  remoteNoStep: 'Zatiaľ nebol nahlásený žiadny krok',
  builtWith: 'Vytvorené pomocou {model}',
  buildStartedChip: 'Vytváranie spustené',
  approvedBuilding: 'Schválené · Vytvára sa…',
  built: 'Vytvorené',
  byAgent: 'cez {agent}',
  agentNameReported: 'Meno uvedené agentom',
  publishedBy: 'Publikoval {agent}',
  elapsed: 'Uplynutý čas',
  ready: '{kind}: hotovo',
  readyGeneric: 'Pripravené',
  confirming: 'Potvrdzovanie uloženia',
  steps: 'Kroky',
  outcomeUnknown: 'Práca sa možno dokončila, ale nepodarilo sa to potvrdiť.',
  checkAgain: 'Skontrolovať znova',
  stopHere: 'Zastaviť tu',
  budgetPaused:
    'Pozastavené na hranici rozpočtu: {spent} z {limit}. Posledná funkčná verzia zostáva konceptom.',
  continueWith: 'Pokračovať s navýšením o {amount}',
  failed: 'Práca sa zastavila pred dokončením.',
  stopped: 'Zastavené. Posledná uložená verzia sa nezmenila.',
  stoppedNeutral: 'Zastavené.',
  blockedApproval: 'Čaká sa na váš súhlas',
  blockedBudget: 'Pozastavené na hranici rozpočtu tejto úlohy',
  blockedProvider: 'Služba modelu momentálne neodpovedá',
  blockedViewer: 'Treba nastaviť osobu, ktorá to bude spúšťať pre čitateľov',
  blockedOther: 'Pozastavené',
  denied: 'K tejto úlohe nemáte prístup.',
  loading: 'Spúšťanie…',
  shaping: 'Získava podobu podľa vašej požiadavky…',
  pendingValue: 'Zatiaľ nevyplnené',
  unavailableValue: 'Nedostupné',
  publishing: 'Publikuje sa…',
  fieldsCount: 'Polia: {n}',
  busyNow: '{model} je práve zaneprázdnený.',
  busyHeld: '{model} je zaneprázdnený.',
  busyStill: '{model} je stále zaneprázdnený.',
  draftSaved: 'Váš koncept je uložený.',
  retryIn: 'Ďalší pokus o {n} s.',
  retryingNow: 'Prebieha ďalší pokus.',
  checkingLastStep: 'Kontroluje sa, či sa posledný krok dokončil…',
  continueFrom: 'Keď bude {model} voľný, bude sa pokračovať krokom „{step}“.',
  tryLater: 'Skúste to znova o pár minút.',
  stoppedAt: 'Zastavené. Váš koncept je uložený pri kroku „{step}“.',
  tryNow: 'Skúsiť teraz',
  tryAgain: 'Skúsiť znova',
  tryWith: 'Skúsiť s {model}',
  altPolicy: 'Nastavenia vašej spoločnosti iný model nepovoľujú.',
  altRegion: 'Iný model nie je dostupný v regióne spracovania vašej spoločnosti.',
  altBudget: 'Iný model by prekročil rozpočet vašej spoločnosti.',
  altNotReady: 'Iný model zatiaľ nie je pripravený na použitie.',
  buildingWith: 'Vytvára sa s {model}',
  buildingVersion: 'Vytvára sa v{n} s {model}',
  versionKept: 'v{kept} zostáva bez zmeny.',
  compareWith: 'Porovnať s v{n}',
  builderNotReady: 'Tento druh artefaktu sa v tomto pracovnom priestore zatiaľ nedá vytvoriť.',
  builderNotReadyState: 'Ešte nie je pripravené',
  builderNothingStarted: 'Žiadne vytváranie sa nespustilo ani nevyúčtovalo.',
  builderNotAllowed: 'Nastavenia vašej spoločnosti nepovoľujú vytváranie tohto druhu artefaktu.',
  builderNotInRegion:
    'Tento druh artefaktu sa nedá vytvoriť v regióne spracovania vašej spoločnosti.',
  builderOverBudget: 'Vytvorenie tohto druhu artefaktu by prekročilo rozpočet vašej spoločnosti.',
  buildLargerTakesMinutes: 'Vytvorenie väčších artefaktov trvá niekoľko minút.',
  startBlankChat: 'Začať z prázdneho chatu',
  elapsedMinSec: '{min} min {s} s',
};

const sl: Record<BuildExperienceCopyKey, string> = {
  phaseQueued: 'Čakanje na začetek',
  phaseAcquiring: 'Pripravljanje delovnega prostora',
  phaseAuthoring: 'Pisanje',
  phaseCompiling: 'Sestavljanje',
  phaseRendering: 'Ustvarjanje predogledov',
  phaseChecking: 'Preverjanje delovanja',
  phaseSaving: 'Shranjevanje',
  phaseSettled: 'Končano',
  stepOf: '{n} od {total}',
  stillWorking: 'Korak {step} še poteka',
  slowReason: 'Korak {step} traja dlje kot običajno.',
  keepWaiting: 'Počakaj še',
  stop: 'Ustavi',
  stopping: 'Ustavljanje…',
  continue: 'Nadaljuj',
  fix: 'Popravi',
  fixInChat: 'Popravi v klepetu',
  remoteWorking: '{agent} dela',
  remoteWorkingUnnamed: 'Povezani agent dela',
  remoteLastSecond: 'Zadnji korak pred {seconds} s',
  remoteLastMinute: 'Zadnji korak pred {minutes} min',
  remoteNoStep: 'Noben korak še ni bil sporočen',
  builtWith: 'Ustvarjeno z modelom {model}',
  buildStartedChip: 'Izdelava se je začela',
  approvedBuilding: 'Odobreno · Ustvarjanje…',
  built: 'Ustvarjeno',
  byAgent: 'prek {agent}',
  agentNameReported: 'Ime, ki ga je sporočil agent',
  publishedBy: 'Objavil: {agent}',
  elapsed: 'Pretečeni čas',
  ready: '{kind}: pripravljeno',
  readyGeneric: 'Pripravljeno',
  confirming: 'Potrjevanje shranjevanja',
  steps: 'Koraki',
  outcomeUnknown: 'Delo je morda končano, vendar tega ni bilo mogoče potrditi.',
  checkAgain: 'Preveri znova',
  stopHere: 'Ustavi tukaj',
  budgetPaused:
    'Zaustavljeno pri omejitvi proračuna: {spent} od {limit}. Zadnja delujoča različica ostane osnutek.',
  continueWith: 'Nadaljuj z dodatnimi {amount}',
  failed: 'Delo se je ustavilo pred zaključkom.',
  stopped: 'Ustavljeno. Zadnja shranjena različica je nespremenjena.',
  stoppedNeutral: 'Ustavljeno.',
  blockedApproval: 'Čakanje na tvojo odobritev',
  blockedBudget: 'Zaustavljeno pri omejitvi proračuna za to opravilo',
  blockedProvider: 'Storitev modela se trenutno ne odziva',
  blockedViewer: 'Nastaviti je treba nekoga, ki bo to izvajal za gledalce',
  blockedOther: 'Zaustavljeno',
  denied: 'Nimaš dostopa do tega opravila.',
  loading: 'Zagon…',
  shaping: 'Oblikovanje na podlagi tvoje zahteve…',
  pendingValue: 'Še ni izpolnjeno',
  unavailableValue: 'Ni na voljo',
  publishing: 'Objavljanje…',
  fieldsCount: 'Polja: {n}',
  busyNow: '{model} je trenutno zaseden.',
  busyHeld: '{model} je zaseden.',
  busyStill: '{model} je še vedno zaseden.',
  draftSaved: 'Tvoj osnutek je shranjen.',
  retryIn: 'Nov poskus čez {n} s.',
  retryingNow: 'Nov poskus poteka.',
  checkingLastStep: 'Preverjanje, ali se je zadnji korak končal…',
  continueFrom: 'Ko bo {model} prost, se bo nadaljevalo pri koraku »{step}«.',
  tryLater: 'Poskusi znova čez nekaj minut.',
  stoppedAt: 'Ustavljeno. Tvoj osnutek je shranjen pri koraku »{step}«.',
  tryNow: 'Poskusi zdaj',
  tryAgain: 'Poskusi znova',
  tryWith: 'Poskusi z modelom {model}',
  altPolicy: 'Nastavitve tvojega podjetja ne dovoljujejo drugega modela.',
  altRegion: 'Drug model ni na voljo v regiji obdelave tvojega podjetja.',
  altBudget: 'Drug model bi presegel proračun tvojega podjetja.',
  altNotReady: 'Drug model še ni pripravljen za uporabo.',
  buildingWith: 'Izdeluje se z modelom {model}',
  buildingVersion: 'v{n} se izdeluje z modelom {model}',
  versionKept: 'v{kept} ostane, kakršna je.',
  compareWith: 'Primerjaj z v{n}',
  builderNotReady: 'Te vrste artefakta v tem delovnem prostoru še ni mogoče izdelati.',
  builderNotReadyState: 'Še ni pripravljeno',
  builderNothingStarted: 'Nobena izdelava ni bila zagnana ali zaračunana.',
  builderNotAllowed: 'Nastavitve tvojega podjetja ne dovoljujejo izdelave te vrste artefakta.',
  builderNotInRegion: 'Te vrste artefakta ni mogoče izdelati v regiji obdelave tvojega podjetja.',
  builderOverBudget: 'Izdelava te vrste artefakta bi presegla proračun tvojega podjetja.',
  buildLargerTakesMinutes: 'Izdelava večjih artefaktov traja nekaj minut.',
  startBlankChat: 'Začni s praznim klepetom',
  elapsedMinSec: '{min} min {s} s',
};

export const buildExperienceCopy: NamespaceCatalog<BuildExperienceCopyKey> = {
  en,
  bg,
  cs,
  da,
  de,
  el,
  es,
  et,
  fi,
  fr,
  ga,
  hr,
  hu,
  it,
  lt,
  lv,
  mt,
  nl,
  pl,
  pt,
  ro,
  sk,
  sl,
  sv,
};
export default buildExperienceCopy;
